import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput, Platform, ActivityIndicator, Modal } from 'react-native';
import { useLocalSearchParams, Stack, useRouter, useFocusEffect } from 'expo-router';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { api, apiGet, apiPost, ApiError } from '../../../src/api/client';
import { useAuth } from '../../../src/context/AuthContext';
import { useToast } from '../../../src/components/Toast';
import Button from '../../../src/components/Button';
import Card from '../../../src/components/Card';
import ReportProblemModal from '../../../src/components/ReportProblemModal';
import WashCompleteOverlay, { WashCompleteData } from '../../../src/components/WashCompleteOverlay';
import CollapsibleSection from '../../../src/components/CollapsibleSection';
import VehicleProfileModal, { SubscriptionVehicle, formatVehicleLine } from '../../../src/components/VehicleProfileModal';
import PageFooter from '../../../src/components/PageFooter';
import { useTheme } from '../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { rs, fs } from '../../../src/utils/responsive';
import { confirmChoice, confirmDestructive } from '../../../src/utils/confirm';
import { bundleItemsSummary } from '../../../src/utils/bundleItems';
import { vacMinutesLabel, vendingCreditLabel } from '../../../src/utils/planBenefits';
import type { ColorTheme } from '../../../src/utils/theme';

interface Bay {
  /** 1.4 P20: add-ons sold at this bay (server-priced) and the ids my plan includes. */
  upsells?: { actionId: string; upsellId: string | null; name: string; priceCents: number }[];
  includedUpsellIds?: string[];
  /** 1.4 R05: per-upsell choice on my membership plan / my bundles ('none' hides, 'included' is free). */
  planUpsellPolicy?: Record<string, 'none' | 'purchasable' | 'included'>;
  bundleUpsellPolicy?: Record<string, 'none' | 'purchasable' | 'included'>;
  inUseMaxWaitMs?: number;
  id: string;
  name: string;
  type: 'self-serve' | 'automatic' | 'vacuum';
  pricePerMinute: number | null;
  minimumCharge: number | null;
  maximumCharge: number | null;
  // freeVacMinutes: tier perk (free vacuum grant) — wave-2 backend field;
  // absent on older payloads, so always optional-chain.
  tiers: { name: string; price: number; enabled?: boolean; durationMs?: number; freeVacMinutes?: number | null }[] | null;
  presets: number[] | null;
  allowCustomAmount: boolean;
  // Optional custom-amount bounds (wave 2, mig 0070). Null/absent = no bound.
  customAmountMinCents?: number | null;
  customAmountMaxCents?: number | null;
  // Owner's effective free-vac redemption window (wave 2; default 120 min).
  vacGrantWindowMinutes?: number | null;
  maxWashTimeMs: number | null;
  locationId: string;
  controllerId: string | null;
  isActive: boolean;
}

// Open free-vac grant (earned with a qualifying wash) at this location.
interface VacGrant {
  id: string;
  locationId: string;
  minutesGranted: number;
  sourceTierName: string | null;
  expiresAt: string | null;
}

interface BayAvailability {
  sellable: boolean; reason: 'ok' | 'out_of_service' | 'in_use' | 'controller_offline' | 'no_controller' | 'not_configured';
  queued: boolean; message: string | null; inUse: boolean; outOfService: boolean; updateRequiredControllerIds: string[];
}

interface ControllerStatus {
  hasController: boolean;
  controllerOnline: boolean;
  controllerId?: string;
  message?: string | null;
}

interface Location {
  id: string;
  ownerId: string;
  name: string;
  acceptingPayments?: boolean;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string | null;
  description: string | null;
  hours: any;
}

interface AvailablePlan {
  id: string;
  name: string;
  description: string | null;
  tierName: string;
  priceMonthly: number;
  maxWashesPerDay: number;
  maxWashesPerMonth: number;
  // Vacuum-minute bucket + vending credit (null = plan doesn't include it;
  // cap 0 = unlimited). Always shown so the full plan value is visible.
  maxVacMinutesPerDay?: number | null;
  maxVacMinutesPerMonth?: number | null;
  vendingCreditCentsPerMonth?: number | null;
  locationScope: string;
  locationName: string | null;
  ownerBusinessName: string;
}

interface BundleCheckItem {
  purchasedBundleId: string;
  planName: string;
  tierName: string;
  tierPrice: number;
  washesRemaining: number;
  washesTotal: number;
  washesUsed: number;
  expiresAt: string | null;
}

// Live membership with this owner that does NOT cover the current bay
// (GET /api/subscriptions/check returns this alongside hasSubscription:false).
interface OtherSubscription {
  planName: string;
  planType: 'automatic' | 'self_serve' | 'mixed';
  tierName: string | null;
  coversAutomatic: boolean;
  coversSelfServe: boolean;
  locationScope: string;
  locationNames: string[];
  reason: 'bay_type' | 'location' | 'tier';
}

interface AvailableBundle {
  id: string;
  name: string;
  description: string | null;
  tierName: string;
  washCount: number;
  items?: {
    kind: 'wash' | 'vac_minutes' | 'vending_credit';
    tierName: string | null;
    quantity: number | null;
    minutes: number | null;
    creditCents: number | null;
    allAtOnce?: boolean;
  }[];
  bundlePrice: number;
  locationScope: string;
  locationName: string | null;
  expirationDays: number | null;
  expirationMinutes?: number | null;
  ownerBusinessName: string;
}

// Vacuum-eligible purchased bundle (GET /api/bundles/check?bayId=<vacuum>).
interface VacBundleItem {
  purchasedBundleId: string;
  planName: string;
  remainingMinutes: number;
  // Item flagged "must be used in one session" — stopping early forfeits
  // the remainder instead of refunding it.
  allAtOnce?: boolean;
  expiresAt: string | null;
}

// Resolve tier name to display label — supports both number ("1"-"4") and name ("Basic", "Premium")
function getTierLabel(tierName: string, tiers: { name: string; price: number }[] | null | undefined): string {
  if (!tiers) return tierName;
  const num = parseInt(tierName);
  if (!isNaN(num) && num >= 1 && num <= tiers.length) return tiers[num - 1].name;
  const match = tiers.find(t => t.name === tierName);
  return match ? match.name : tierName;
}

function getTierPrice(tierName: string, tiers: { name: string; price: number }[] | null | undefined): number {
  if (!tiers) return 0;
  const num = parseInt(tierName);
  if (!isNaN(num) && num >= 1 && num <= tiers.length) return tiers[num - 1].price;
  const match = tiers.find(t => t.name === tierName);
  return match ? match.price : 0;
}

// "2 hours" when divisible by 60, else "90 minutes" — for the free-vac note.
function formatGrantWindow(minutes: number): string {
  if (minutes > 0 && minutes % 60 === 0) {
    const h = minutes / 60;
    return `${h} hour${h === 1 ? '' : 's'}`;
  }
  return `${minutes} minutes`;
}

// Vacuum minutes remaining shown with one decimal ("12.5"), trailing .0
// stripped ("12") — Nathan 2026-08-17.
function formatMinutes1(n: number): string {
  const s = Math.max(0, n).toFixed(1);
  return s.endsWith('.0') ? s.slice(0, -2) : s;
}

const CODE_TO_REASON: Record<string, string> = {
  NO_PAYMENT_METHOD: 'no_payment_method',
  OWNER_NOT_ACTIVATED: 'owner_not_activated',
  CONTROLLER_OFFLINE: 'controller_offline',
  BAY_DEACTIVATED: 'bay_deactivated',
  STRIPE_NOT_ONBOARDED: 'stripe_not_onboarded',
  BAY_ACTIVE: 'bay_active',
  PAYMENT_FAILED: 'payment_failed',
  BUNDLE_EXPIRED: 'bundle_expired',
  SUBSCRIPTION_LIMIT: 'subscription_limit',
  FLEET_INACTIVE: 'fleet_inactive',
};

function getFailureReason(err: any): string {
  if (err instanceof ApiError && err.code) {
    return CODE_TO_REASON[err.code] || 'generic';
  }
  const msg = err?.message?.toLowerCase() || '';
  if (msg.includes('equipment') || msg.includes('controller') || msg.includes('offline')) return 'controller_offline';
  if (msg.includes('payment')) return 'payment_failed';
  return 'generic';
}

// Client-generated idempotency token for bundle purchases: retrying the same
// confirm with the same token returns the original purchase (no double charge).
// crypto.randomUUID exists on web; Hermes lacks it, so fall back to a
// Math.random RFC4122-v4 shape (no uuid lib in this app).
function makePurchaseToken(): string {
  const g = globalThis as any;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Plain-English summary of a membership that doesn't cover this bay.
function describeOtherSubscription(o: OtherSubscription): string {
  let covers: string;
  if (o.coversAutomatic && o.coversSelfServe) covers = 'automatic and self-serve washes';
  else if (o.coversAutomatic) covers = `${o.tierName ? `${o.tierName} ` : ''}automatic washes`;
  else if (o.coversSelfServe) covers = 'self-serve washing';
  else covers = 'washes';
  const where = o.locationScope === 'all'
    ? ' at all locations'
    : o.locationNames?.length ? ` at ${o.locationNames.join(', ')}` : '';
  const why = o.reason === 'bay_type'
    ? 'this bay is a different wash type'
    : o.reason === 'location'
      ? "this location isn't part of your plan"
      : "this bay's wash tier isn't included";
  return `You have the ${o.planName} membership with this car wash. It covers ${covers}${where} — this bay isn't included (${why}). You can still pay per wash or use bundles here.`;
}

/** WashScreen - bay selection, tier/preset pricing, payment, and live wash session controls. */
export default function WashScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { locationId, bayId, voucher: voucherParam } = useLocalSearchParams<{ locationId: string; bayId: string; voucher?: string }>();
  const [bay, setBay] = useState<Bay | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  // Self-serve timed package pick (bay.tiers on self-serve bays); mutually
  // exclusive with selectedPreset / custom amount.
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [customAmountText, setCustomAmountText] = useState('');
  const [customAmountError, setCustomAmountError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [controllerStatus, setControllerStatus] = useState<ControllerStatus | null>(null);
  const [controllerCheckLoading, setControllerCheckLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [timerMessage, setTimerMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Count-up session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionIsSubscription, setSessionIsSubscription] = useState(false);
  const [sessionDailyMinutesAllowed, setSessionDailyMinutesAllowed] = useState<number | null>(null);
  const [sessionMonthlyMinutesAllowed, setSessionMonthlyMinutesAllowed] = useState<number | null>(null);
  // Bundle-funded count-up session (POST /api/bundles/authorize-vacuum session
  // mode): the claim caps the session; stopping early refunds unused minutes
  // (minimum-minutes rule applies; allAtOnce forfeits the remainder).
  const [sessionIsBundle, setSessionIsBundle] = useState(false);
  const [sessionClaimedMinutes, setSessionClaimedMinutes] = useState<number | null>(null);
  const [sessionAllAtOnce, setSessionAllAtOnce] = useState(false);
  // Effective minimum minutes for membership/bundle sessions (0 = none, e.g.
  // plan.overrideMinimums). Drives the stop-early warning only — the backend
  // enforces the actual deduction.
  const [sessionMinMinutes, setSessionMinMinutes] = useState(0);
  // Shared "cap reached" latch for membership AND bundle sessions.
  const subLimitHitRef = useRef(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [minimumCharge, setMinimumCharge] = useState(300);
  const [maximumCharge, setMaximumCharge] = useState<number | null>(null);
  const [preAuthorized, setPreAuthorized] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  // Preset countdown state
  const [presetMode, setPresetMode] = useState(false);
  const [presetAmount, setPresetAmount] = useState(0);
  const [presetCreditApplied, setPresetCreditApplied] = useState(0);
  const [presetVoucherApplied, setPresetVoucherApplied] = useState(0);
  const [presetPointsEarned, setPresetPointsEarned] = useState(0);
  const [presetDurationMs, setPresetDurationMs] = useState(0);
  const [presetEndsAt, setPresetEndsAt] = useState<Date | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [confirmStop, setConfirmStop] = useState(false);

  // Fleet state
  const [fleetAccounts, setFleetAccounts] = useState<{ accountId: string; companyName: string; status: string }[]>([]);
  const [selectedFleetId, setSelectedFleetId] = useState<string | null>(null);
  const [fleetSessionId, setFleetSessionId] = useState<string | null>(null);
  const fleetSessionIdRef = useRef<string | null>(null);
  const selectedFleetIdRef = useRef<string | null>(null);

  // Subscription/membership state — covers both automatic (tier/wash) and self-serve (minute) shapes
  const [subCheck, setSubCheck] = useState<{
    hasSubscription: boolean;
    canWash: boolean;
    bayType?: 'automatic' | 'self-serve' | 'vacuum';
    subscriptionId: string;
    planName: string;
    planType?: 'automatic' | 'self_serve' | 'mixed';
    tierName?: string | null;
    tierPrice?: number;
    washesUsedToday?: number;
    washesUsedThisMonth?: number;
    maxWashesPerDay?: number;
    maxWashesPerMonth?: number;
    dailyRemaining?: number | null;
    monthlyRemaining?: number | null;
    minutesUsedToday?: number;
    minutesUsedThisMonth?: number;
    maxMinutesPerDay?: number;
    maxMinutesPerMonth?: number;
    dailyMinutesRemaining?: number | null;
    monthlyMinutesRemaining?: number | null;
    // Vacuum minute bucket (vacuum bays only); remaining null = unlimited.
    vacMinutesUsedToday?: number;
    vacMinutesUsedThisMonth?: number;
    maxVacMinutesPerDay?: number | null;
    maxVacMinutesPerMonth?: number | null;
    dailyVacMinutesRemaining?: number | null;
    monthlyVacMinutesRemaining?: number | null;
    // Registered vehicle (mig 0055); null until the member creates one.
    vehicle?: SubscriptionVehicle | null;
    // Owner/admin suspension (mig 0056): redemptions blocked, billing paused.
    suspended?: boolean;
  } | null>(null);
  // Vehicle profile flow: form modal + pre-redemption "this vehicle is here"
  // confirmation. pendingRedeemKind remembers which redemption to resume after
  // a member registers their vehicle mid-flow.
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [redeemConfirm, setRedeemConfirm] = useState<'automatic' | 'selfserve' | null>(null);
  const [pendingRedeemKind, setPendingRedeemKind] = useState<'automatic' | 'selfserve' | null>(null);
  // The subscription just purchased on this screen. Drives the post-purchase
  // vehicle prompt independently of whether the new membership covers THIS bay
  // (subCheck says hasSubscription:false when it doesn't — real case: plan
  // bought from a bay another tier covers).
  const [purchasedSub, setPurchasedSub] = useState<{ id: string; vehicle: SubscriptionVehicle | null } | null>(null);
  const [availablePlans, setAvailablePlans] = useState<AvailablePlan[]>([]);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  // Membership with this owner that does NOT cover this bay — informational card only.
  const [otherSub, setOtherSub] = useState<OtherSubscription | null>(null);
  // Prominent post-purchase confirmation modal so a completed buy is unmissable
  // (Nathan: customers were accidentally buying twice).
  const [purchaseSuccess, setPurchaseSuccess] = useState<{
    kind: 'bundle' | 'subscription'; headline: string; detail: string; readyLine: string;
  } | null>(null);
  const [washComplete, setWashComplete] = useState<(WashCompleteData & { transactionId?: string }) | null>(null);

  // Bundle state
  const [bundleCheck, setBundleCheck] = useState<{ hasBundles: boolean; bundles: BundleCheckItem[] } | null>(null);
  const [availableBundles, setAvailableBundles] = useState<AvailableBundle[]>([]);
  // After buying a pack we scroll the customer to the "use a prepaid wash"
  // section (which sits above where they tapped Buy). scrollRef drives the
  // scroll; bundleUseY tracks that section's offset; pendingBundleFocus arms it.
  const scrollRef = useRef<ScrollView>(null);
  const bundleUseY = useRef(0);
  const [pendingBundleFocus, setPendingBundleFocus] = useState(false);
  const [purchasingBundle, setPurchasingBundle] = useState<string | null>(null);

  // ─── Vacuum state (bay.type === 'vacuum') ───
  // No live session polling exists for vacuums; the running screen is a simple
  // client-side countdown from the tier's durationMs (or grant minutes).
  const [vacGrants, setVacGrants] = useState<VacGrant[]>([]);
  const [vacBundles, setVacBundles] = useState<VacBundleItem[]>([]);
  // Countdown kinds are paid packages + free grants only — membership and
  // bundle vacuums run as live count-UP sessions (sessionActive machinery).
  // Wave 2: paid + grant runs are now REAL server sessions (wash_sessions
  // rows, funding 'prepaid'/'grant') — sessionId enables the generic stop
  // and the restore/return-banner flows. Absent on older backends, the UI
  // degrades to the old client-only countdown (stop via the legacy grant
  // endpoint; paid runs simply run out).
  const [vacRunning, setVacRunning] = useState<{
    tierName: string;
    minutes: number;
    endsAt: number;           // epoch ms
    amountCents: number;
    kind: 'paid' | 'grant';
    transactionId?: string;
    sessionId?: string;
  } | null>(null);
  const [vacRemainingSec, setVacRemainingSec] = useState(0);
  const [vacDone, setVacDone] = useState(false);
  // Early-stop of a running countdown vacuum (paid or grant). Always warned
  // first — there is NO refund of money or unused minutes.
  const [vacStopping, setVacStopping] = useState(false);

  // Report a Problem state
  const [showReport, setShowReport] = useState(false);
  const [reportTransactionId, setReportTransactionId] = useState('');

  // Mobile web app download banner
  const [dismissedBanner, setDismissedBanner] = useState(false);

  // ─── Voucher (Phase 2): when arriving via /redeem/<code> the redemption
  // landing page hands off `?voucher=CODE`. We look it up, validate against
  // the current bay's owner/location/tier scope, and apply it to the purchase
  // automatically. No picker UI yet — picker comes in a later phase when
  // customers have multiple active vouchers worth choosing between. ───
  type EligibleVoucher = {
    code: string;
    kind: 'gift' | 'promo' | 'points_redemption';
    discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_upsell';
    percentOff: number | null;
    /** 1.4 R05 */
    discountScope?: 'wash' | 'wash_and_upsells';
    freeUpsellIds?: string[];
    tierName: string | null;
    freeMinutes: number | null;
    bayType: 'self-serve' | 'automatic' | null;
    /** Multi-select scope (mig 0050/0064); authoritative when present. */
    appliesTo?: { tierIds: string[]; selfServe: boolean; vacuum?: boolean; vending?: boolean } | null;
    locationId: string | null;
    ownerName: string;
  };
  const [appliedVoucher, setAppliedVoucher] = useState<EligibleVoucher | null>(null);
  const [availableVouchers, setAvailableVouchers] = useState<EligibleVoucher[]>([]);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const [washCreditCents, setWashCreditCents] = useState<number | null>(null);
  const [useWashCredit, setUseWashCredit] = useState(false);
  // 1.4 P20: live bay availability (polled every 10 s on automatic bays), chosen add-ons, queued wait.
  const [availability, setAvailability] = useState<BayAvailability | null>(null);
  const [selectedUpsells, setSelectedUpsells] = useState<string[]>([]);
  const [queuedWait, setQueuedWait] = useState<{ programId: string; complete: WashCompleteData & { transactionId?: string }; message: string | null } | null>(null);
  // A tier-scoped voucher (e.g. "25% off Ultimate") only discounts its own tier.
  // The backend rejects mismatches (VOUCHER_TIER_MISMATCH), so pricing + the
  // purchase call must treat the voucher as inactive when another tier is picked.
  const voucherAppliesToSelectedTier = !!appliedVoucher
    && (appliedVoucher.tierName == null || appliedVoucher.tierName === selectedTier);
  const isMobileWeb = Platform.OS === 'web' && typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const { isAuthenticated, isLoading: authLoading, role, user } = useAuth();
  const router = useRouter();

  // ── Dual-family pricing (2026-08-09): self-serve bays AND vacuums can carry
  // BOTH families at once — Metered (pricePerMinute + maximumCharge → count-up
  // sessions, custom amounts) and Timed Packages (tiers with durationMs).
  // A vacuum with a rate runs the full self-serve session flow; packages and
  // sessions render together when both are configured. Fleet UI stays OFF for
  // all vacuums; vouchers ARE offered at vacuums (mig 0064 — appliesTo.vacuum,
  // server-filtered via /api/me/vouchers?bayId=). ──
  const isMeteredVac = bay?.type === 'vacuum' && bay.pricePerMinute != null;
  const isSessionBay = bay?.type === 'self-serve' || isMeteredVac;
  // Metered family enabled = max charge set (clearing it disables count-up).
  const countUpAvailable = isSessionBay && bay?.maximumCharge != null;
  const sessionNoun = bay?.type === 'vacuum' ? 'vacuum' : 'wash';
  // Timed packages purchasable via the session flow (self-serve bays only —
  // vacuum packages keep their own tier card, which fires charge-then-run).
  const selfServePackages = (bay?.type === 'self-serve' && bay.tiers)
    ? bay.tiers.filter(t => t.enabled !== false && t.durationMs)
    : [];

  // Auth guard — redirect unauthenticated users to login with returnTo
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      router.replace(`/login?returnTo=/wash/${locationId}/${bayId}`);
    }
  }, [authLoading, isAuthenticated]);

  const homeBackButton = () => (
    <TouchableOpacity
      onPress={() => {
        const home = role === 'owner' ? '/owner' : role === 'admin' ? '/admin' : '/user';
        router.replace(home as any);
      }}
      hitSlop={8}
      style={{ marginRight: 8 }}
    >
      <Ionicons name="arrow-back" size={24} color={colors.primary} />
    </TouchableOpacity>
  );
  const toast = useToast();

  /** 1.4 P20: equipment-state refusals (OOS / in use / update required) carry the customer-facing text in `message`. */
  const failMsg = (err: any): string | undefined => {
    const d = err?.details as { code?: string; message?: string } | undefined;
    if (d && ['BAY_OUT_OF_SERVICE', 'BAY_IN_USE', 'UPDATE_REQUIRED', 'UPSELL_UNAVAILABLE', 'UPSELL_CHARGE_FAILED', 'NOT_CONFIGURED'].includes(d.code ?? '') && d.message) return d.message;
    return err?.message;
  };
  const routeToFailure = (reason: string, message?: string) => {
    const params = new URLSearchParams({ reason });
    if (message) params.set('message', message);
    router.replace(`/wash/failed?${params.toString()}`);
  };

  // Unverified customers get 3 grace purchases, then the backend 403s with
  // EMAIL_VERIFICATION_REQUIRED — route them to the verify screen (returnTo
  // brings them straight back here to finish the purchase) instead of the
  // generic failure page. Returns true when handled.
  const redirectIfVerifyRequired = (err: any): boolean => {
    if (err instanceof ApiError && err.code === 'EMAIL_VERIFICATION_REQUIRED') {
      router.push({
        pathname: '/verify-email',
        params: { email: user?.email ?? '', returnTo: `/wash/${locationId}/${bayId}` },
      } as never);
      return true;
    }
    return false;
  };

  // Redirect to failure page for pre-purchase blockers
  useEffect(() => {
    if (!bay || !location) return;
    if (!bay.isActive) {
      routeToFailure('bay_deactivated', `${bay.name} at ${location.name} is currently turned off.`);
    } else if (location.acceptingPayments === false && fleetAccounts.length === 0) {
      routeToFailure('owner_not_activated', `${location.name} is not currently accepting payments.`);
    }
  }, [bay, location, fleetAccounts]);

  // 1.4 P20: poll availability while an automatic bay screen is open.
  useEffect(() => {
    if (!bay || bay.type !== 'automatic' || washComplete) return;
    let stop = false;
    const tick = async () => {
      try { const a = await api<BayAvailability>(`/api/bays/${bayId}/availability`); if (!stop) setAvailability(a); } catch { /* keep last */ }
    };
    tick();
    const t = setInterval(tick, 10000);
    return () => { stop = true; clearInterval(t); };
  }, [bay?.id, bay?.type, bayId, !!washComplete]);

  // 1.4 P20: a queued wash waits for the bay; poll the run until it starts or refunds.
  useEffect(() => {
    if (!queuedWait) return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api<{ started: boolean; refunded: boolean; failed: boolean; terminal: boolean }>(`/api/me/program-runs/${queuedWait.programId}`);
        if (stop) return;
        if (r.started) { setWashComplete({ ...queuedWait.complete, queued: false }); setQueuedWait(null); }
        else if (r.refunded || r.failed) { setQueuedWait(null); routeToFailure('bay_busy_timeout', 'The bay did not free up in time. Your wash was refunded automatically.'); }
      } catch { /* keep polling */ }
    };
    const t = setInterval(tick, 3000);
    return () => { stop = true; clearInterval(t); };
  }, [queuedWait?.programId]);

  // Notify owner when customer scans bay with no controller
  useEffect(() => {
    if (bay && !bay.controllerId) {
      api(`/api/bays/${bayId}/controller-status`).catch(() => {});
    }
  }, [bay?.controllerId]);

  useEffect(() => {
    const ac = new AbortController();
    const fetchData = async () => {
      try {
        const [bayData, locData] = await Promise.all([
          api<Bay>(`/api/bays/${bayId}`),
          api<Location>(`/api/locations/${locationId}`),
        ]);
        if (ac.signal.aborted) return;
        setBay(bayData);
        setLocation(locData);
        if ((bayData.type === 'automatic' || bayData.type === 'vacuum') && bayData.tiers?.length) {
          const firstEnabled = bayData.tiers.find(t => t.enabled !== false);
          if (firstEnabled) setSelectedTier(firstEnabled.name);
        }
        // Check controller reachability (auto-retry once if offline)
        if (bayData.controllerId) {
          try {
            const ctrlStatus = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
            if (ac.signal.aborted) return;
            if (ctrlStatus.hasController && !ctrlStatus.controllerOnline) {
              // Retry once after 2s — controller may have just sent a heartbeat
              await new Promise(r => setTimeout(r, 2000));
              if (ac.signal.aborted) return;
              try {
                const retry = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
                if (ac.signal.aborted) return;
                setControllerStatus(retry);
              } catch { /* retry also failed — use original status */
                if (ac.signal.aborted) return;
                setControllerStatus(ctrlStatus);
              }
            } else {
              setControllerStatus(ctrlStatus);
            }
          } catch { /* non-fatal */ }
        }
        if (ac.signal.aborted) return;
        // Fetch fleet memberships and subscription check if authenticated
        if (isAuthenticated && (role === 'user' || role === 'owner')) {
          // Vacuum: check for an open free-vac grant at this location, plus
          // bundles with vac minutes and purchasable vac bundles.
          if (bayData.type === 'vacuum') {
            try {
              const g = await apiGet<{ grants: VacGrant[] }>(`/api/me/vac-grants?locationId=${locationId}`);
              if (ac.signal.aborted) return;
              setVacGrants(g.grants ?? []);
            } catch { /* silent: no grants or fetch failed */ }
            if (ac.signal.aborted) return;
            try {
              const bc = await apiGet<{ hasBundles: boolean; bundles: VacBundleItem[] }>(`/api/bundles/check?bayId=${bayId}`);
              if (ac.signal.aborted) return;
              setVacBundles(bc.bundles ?? []);
            } catch { /* silent */ }
            if (ac.signal.aborted) return;
            try {
              const avail = await apiGet<AvailableBundle[]>(`/api/bundles/available?bayId=${bayId}`);
              if (ac.signal.aborted) return;
              setAvailableBundles(avail);
            } catch { /* no bundles available */ }
            if (ac.signal.aborted) return;
          }
          try {
            const memberships = await apiGet<{ accountId: string; companyName: string; status: string }[]>('/api/fleet/me/memberships');
            if (ac.signal.aborted) return;
            setFleetAccounts(memberships.filter(m => m.status === 'active'));
          } catch { /* not a fleet member */ }
          if (ac.signal.aborted) return;
          // Check subscription for this bay (works for both automatic and self-serve)
          {
            try {
              const check = await apiGet<any>(`/api/subscriptions/check?bayId=${bayId}`);
              if (ac.signal.aborted) return;
              if (check.hasSubscription) {
                setSubCheck(check);
              } else {
                // Live membership with this owner that doesn't cover this bay —
                // surface an informational card instead of the plans list.
                setOtherSub(check.otherSubscription ?? null);
                // No subscription — fetch available plans that cover this bay
                try {
                  const plans = await apiGet<AvailablePlan[]>(`/api/subscriptions/available?bayId=${bayId}`);
                  if (ac.signal.aborted) return;
                  setAvailablePlans(plans);
                } catch { /* no plans available */ }
              }
            } catch { /* subscription check failed — try fetching plans anyway */
              if (ac.signal.aborted) return;
              try {
                const plans = await apiGet<AvailablePlan[]>(`/api/subscriptions/available?bayId=${bayId}`);
                if (ac.signal.aborted) return;
                setAvailablePlans(plans);
              } catch { /* no plans */ }
            }
          }
          if (bayData.type === 'automatic') {

            if (ac.signal.aborted) return;
            // Check bundles for this bay
            try {
              const bCheck = await apiGet<{ hasBundles: boolean; bundles: BundleCheckItem[] }>(`/api/bundles/check?bayId=${bayId}`);
              if (ac.signal.aborted) return;
              setBundleCheck(bCheck);
            } catch { /* bundle check failed — still offer purchasable packs below */ }
            // Always fetch purchasable packs so owning one bundle doesn't hide
            // the rest — customers can buy additional or different packs.
            // bayId (not ownerId) so the backend hides bundles whose tier or
            // location scope this bay can't redeem.
            try {
              const avail = await apiGet<AvailableBundle[]>(`/api/bundles/available?bayId=${bayId}`);
              if (ac.signal.aborted) return;
              setAvailableBundles(avail);
            } catch { /* no bundles available */ }
          }
        }
      } catch {
        if (ac.signal.aborted) return;
        toast.show({ type: 'error', title: 'Error', message: 'Could not load wash information' });
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };
    fetchData();
    return () => { ac.abort(); if (timerRef.current) clearInterval(timerRef.current); };
  }, [bayId, locationId]);

  // Wash credit lookup: fetch any per-owner balance for this customer.
  // If there's a balance at this owner, we surface a "Use $X credit"
  // toggle. The customer picks credit OR voucher, never both (no stacking).
  useEffect(() => {
    if (!location) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api<{ credits: { ownerId: string; balanceCents: number }[] }>('/api/me/wash-credit');
        if (cancelled) return;
        const match = res.credits.find(c => c.ownerId === location.ownerId);
        setWashCreditCents(match ? match.balanceCents : 0);
        // Credit applies by default — the toggle stays visible so the
        // customer can untick it to save the balance for later.
        if (match && match.balanceCents > 0) setUseWashCredit(true);
      } catch { /* silent: anon visitor or fetch error */ }
    })();
    return () => { cancelled = true; };
  }, [location]);

  // Voucher discovery: fetch this user's active vouchers and surface those
  // matching the current bay as opt-in choices (NOT auto-applied). Customer
  // taps "Apply" to opt in; "Remove" clears the applied voucher.
  // ?bayId= makes the server do the applicability filtering (incl. the
  // vacuum axis on appliesTo, mig 0064) — no client-side scope re-derivation.
  useEffect(() => {
    if (voucherParam) return;     // explicit URL voucher takes priority
    if (!bay || !location) return;
    if (bay.type !== 'automatic' && bay.type !== 'self-serve' && bay.type !== 'vacuum') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api<{ vouchers: EligibleVoucher[] }>(`/api/me/vouchers?bayId=${encodeURIComponent(bayId)}`);
        if (cancelled) return;
        const eligible = [...res.vouchers];
        // Sort best-first (100% > higher % off > minutes > anything else)
        eligible.sort((a, b) => {
          const av = a.discountKind === 'free_wash' ? 100 : (a.percentOff ?? 0);
          const bv = b.discountKind === 'free_wash' ? 100 : (b.percentOff ?? 0);
          return bv - av;
        });
        setAvailableVouchers(eligible);
      } catch { /* not signed in or fetch failed — silent */ }
    })();
    return () => { cancelled = true; };
  }, [bay, location, voucherParam]);

  const handleApplyVoucher = (v: EligibleVoucher) => {
    if (v.tierName && bay?.type === 'automatic') setSelectedTier(v.tierName);
    setAppliedVoucher(v);
  };
  const handleClearVoucher = () => setAppliedVoucher(null);

  // Voucher lookup: when ?voucher=CODE is in the URL, fetch and validate it
  // against the current bay's scope. Runs after the bay/location load so we
  // can compare against ownerId/locationId/type/tier.
  useEffect(() => {
    if (!voucherParam || !bay || !location) return;
    let cancelled = false;
    (async () => {
      try {
        const v = await api<{
          code: string;
          kind: 'gift' | 'promo' | 'points_redemption';
          discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_vend';
          percentOff: number | null;
          tierName: string | null;
          freeMinutes: number | null;
          bayType: 'self-serve' | 'automatic' | null;
          appliesTo?: { tierIds: string[]; selfServe: boolean; vacuum?: boolean; vending?: boolean } | null;
          locationId: string | null;
          status: 'pending' | 'active' | 'redeemed' | 'expired' | 'revoked';
          ownerName: string;
          redemptionsRemaining: number;
        }>(`/api/vouchers/by-code/${encodeURIComponent(voucherParam)}`, { skipAuth: true });
        if (cancelled) return;
        if (v.status !== 'active' && v.status !== 'pending') {
          setVoucherError(`This gift is ${v.status} and can no longer be used.`);
          return;
        }
        if (v.redemptionsRemaining <= 0) {
          setVoucherError('This gift has already been redeemed.');
          return;
        }
        if (v.locationId && v.locationId !== location.id) {
          setVoucherError(`This gift is only valid at a different location.`);
          return;
        }
        if (v.discountKind === 'free_vend') {
          setVoucherError('This gift is for vending machines — scan the QR on a vending machine to use it.');
          return;
        }
        if (bay.type === 'vacuum') {
          // Vacuum scope lives on the appliesTo axis only (mig 0064); the
          // legacy bayType column is a shim and never says 'vacuum'.
          if (!v.appliesTo?.vacuum) {
            setVoucherError('This gift is not valid at vacuums.');
            return;
          }
        } else if (v.bayType && v.bayType !== bay.type) {
          setVoucherError(`This gift is only valid for ${v.bayType} bays.`);
          return;
        }
        // Tier mismatch is not a hard error here — we'll auto-select the matching tier
        if (v.tierName && bay.type === 'automatic') {
          setSelectedTier(v.tierName);
        }
        setAppliedVoucher({
          code: v.code,
          kind: v.kind,
          discountKind: v.discountKind as EligibleVoucher['discountKind'],
          percentOff: v.percentOff,
          tierName: v.tierName,
          freeMinutes: v.freeMinutes,
          bayType: v.bayType,
          appliesTo: v.appliesTo ?? null,
          locationId: v.locationId,
          ownerName: v.ownerName,
        });
      } catch (e: any) {
        if (cancelled) return;
        setVoucherError(e instanceof ApiError ? (e.message || 'Voucher not found') : 'Voucher not found');
      }
    })();
    return () => { cancelled = true; };
  }, [voucherParam, bay, location]);

  useEffect(() => {
    if (sessionActive && sessionStartTime) {
      timerRef.current = setInterval(() => {
        if (presetMode && presetEndsAt) {
          const remaining = Math.max(0, Math.ceil((presetEndsAt.getTime() - Date.now()) / 1000));
          setRemainingSeconds(remaining);
          if (remaining <= 0) {
            setSessionActive(false);
            // Mark preset session completed on backend (retry to ensure bay is released)
            const fSid = fleetSessionIdRef.current;
            const fAid = selectedFleetIdRef.current;
            if (fSid && fAid) {
              apiPost('/api/fleet/me/stop-wash', { sessionId: fSid, fleetAccountId: fAid }).catch(() => {});
            } else if (sessionIdRef.current) {
              const sid = sessionIdRef.current;
              (async () => {
                for (let i = 0; i < 3; i++) {
                  try { await api('/api/wash-sessions/preset-complete', { method: 'POST', body: { sessionId: sid } }); return; } catch { if (i < 2) await new Promise(r => setTimeout(r, 2000)); }
                }
              })();
            }
            sessionIdRef.current = null;
            setFleetSessionId(null);
            setWashComplete({
              washType: 'self-serve', washId: sessionId!,
              amount: presetAmount,
              status: 'completed',
              locationName: location?.name, bayName: bay?.name,
              transactionId: sessionId!,
              creditUsedCents: presetCreditApplied || undefined,
              promoUsedCents: presetVoucherApplied || undefined,
              pointsEarned: presetPointsEarned || undefined,
            });
          }
        } else {
          const elapsed = Math.floor((Date.now() - sessionStartTime.getTime()) / 1000);
          setElapsedSeconds(elapsed);
          // Subscription self-serve: when the daily or monthly allotment runs
          // out the controller/server has stopped the wash. Show the wash-complete
          // screen (rate + report a problem) just like a paid self-serve wash, and
          // refresh the membership card so the depleted limit is reflected instead
          // of leaving a stale "Start with Subscription" button that 429s.
          if (sessionIsSubscription && !subLimitHitRef.current) {
            const daily = sessionDailyMinutesAllowed ?? Infinity;
            const monthly = sessionMonthlyMinutesAllowed ?? Infinity;
            const limitMin = Math.min(daily, monthly);
            if (isFinite(limitMin) && elapsed / 60 >= limitMin) {
              subLimitHitRef.current = true;
              const which = monthly <= daily ? 'monthly' : 'daily';
              const endedId = sessionIdRef.current || sessionId;
              setSessionActive(false);
              setSessionIsSubscription(false);
              sessionIdRef.current = null;
              setSessionStartTime(null);
              setElapsedSeconds(0);
              // Re-pull subscription eligibility so the card flips to the
              // "limit reached" state (prevents the stale-button 429).
              apiGet<any>(`/api/subscriptions/check?bayId=${bayId}`)
                .then(c => { if (c?.hasSubscription) setSubCheck(c); })
                .catch(() => {});
              toast.show({ type: 'info', title: `You've reached your ${which} limit`, message: 'Your membership wash ended.' });
              if (endedId) {
                setWashComplete({
                  washType: 'self-serve', washId: endedId,
                  durationMinutes: limitMin,
                  status: 'completed',
                  locationName: location?.name, bayName: bay?.name,
                  transactionId: endedId,
                });
              }
              setSessionId(null);
            }
          }
          // Bundle session: the claim is the cap. The server auto-closes at
          // maxWashEndTime (full claim consumed, no refund); mirror the
          // membership pattern — end the UI locally and refresh remaining.
          if (sessionIsBundle && !subLimitHitRef.current && sessionClaimedMinutes != null) {
            if (elapsed / 60 >= sessionClaimedMinutes) {
              subLimitHitRef.current = true;
              const endedId = sessionIdRef.current || sessionId;
              setSessionActive(false);
              setSessionIsBundle(false);
              sessionIdRef.current = null;
              setSessionStartTime(null);
              setElapsedSeconds(0);
              apiGet<{ hasBundles: boolean; bundles: VacBundleItem[] }>(`/api/bundles/check?bayId=${bayId}`)
                .then(bc => setVacBundles(bc.bundles ?? []))
                .catch(() => {});
              if (endedId) {
                setWashComplete({
                  washType: 'self-serve', washId: endedId,
                  durationMinutes: sessionClaimedMinutes,
                  status: 'completed',
                  locationName: location?.name, bayName: bay?.name,
                  transactionId: endedId,
                });
              }
              setSessionId(null);
            }
          }
        }
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [sessionActive, sessionStartTime, presetMode, presetEndsAt]);

  // Auto-stop count-up sessions on navigation away (not preset — those run to completion)
  const presetModeRef = useRef(false);
  useEffect(() => { presetModeRef.current = presetMode; }, [presetMode]);
  // Prepaid (bundle/membership) sessions survive navigation — the global
  // return banner is the way back. Only card-metered sessions auto-stop on
  // blur. Paid/grant vacuum COUNTDOWN sessions (wave 2) are also prepaid but
  // never enter sessionIdRef at all (vacRunning.sessionId instead), so they
  // are exempt from the blur auto-stop by construction.
  const prepaidSessionRef = useRef(false);
  useEffect(() => { prepaidSessionRef.current = sessionIsBundle || sessionIsSubscription; }, [sessionIsBundle, sessionIsSubscription]);
  useEffect(() => { fleetSessionIdRef.current = fleetSessionId; }, [fleetSessionId]);
  useEffect(() => { selectedFleetIdRef.current = selectedFleetId; }, [selectedFleetId]);

  useFocusEffect(
    useCallback(() => {
      // On focus: check if there's an active session for this bay → resume countdown or show rating
      if (!sessionIdRef.current) {
        (async () => {
          try {
            const active = await api<any>('/api/wash-sessions/active');
            if (active?.active && active.bayId === bayId) {
              const vacFunding = active.fundingKind ?? active.paymentMethod;
              const isVacCountdown = vacFunding === 'prepaid' || vacFunding === 'grant';
              if (!isVacCountdown && active.isPreset && active.presetEndsAt) {
                const endsAt = new Date(active.presetEndsAt);
                if (endsAt.getTime() <= Date.now()) {
                  // Preset expired while user was away — mark complete and show
                  // rating. The rewards receipt (promo/credit/points) lives on
                  // the session endpoint since this remount lost local state.
                  await api('/api/wash-sessions/preset-complete', { method: 'POST', body: { sessionId: active.sessionId } }).catch(() => {});
                  const receipt = await api<{
                    totalChargedCents: number; creditAppliedCents?: number;
                    voucherDiscountCents?: number; pointsEarned?: number;
                  }>(`/api/wash-sessions/${active.sessionId}`).catch(() => null);
                  setWashComplete({
                    washType: 'self-serve', washId: active.sessionId,
                    amount: receipt?.totalChargedCents ?? active.presetAmountCents,
                    status: 'completed',
                    locationName: active.locationName, bayName: active.bayName,
                    transactionId: active.sessionId,
                    creditUsedCents: receipt?.creditAppliedCents || undefined,
                    promoUsedCents: receipt?.voucherDiscountCents || undefined,
                    pointsEarned: receipt?.pointsEarned || undefined,
                  });
                } else {
                  // Preset still active — resume countdown
                  const startTime = new Date(active.startedAt);
                  const durationMs = endsAt.getTime() - startTime.getTime();
                  setSessionId(active.sessionId);
                  sessionIdRef.current = active.sessionId;
                  setPresetMode(true);
                  setPresetAmount(active.presetAmountCents || 0);
                  setPresetDurationMs(durationMs);
                  setPresetEndsAt(endsAt);
                  const remSec = Math.max(0, Math.ceil((endsAt.getTime() - Date.now()) / 1000));
                  setRemainingSeconds(remSec);
                  setSessionStartTime(new Date(active.startedAt));
                  setSessionActive(true);
                }
              } else if (isVacCountdown) {
                // Prepaid/grant vacuum COUNTDOWN session (wave 2) — restore
                // the countdown UI, not the count-up machinery. These are
                // fully paid up front, so no sessionIdRef (no auto-stop on
                // blur) and navigation stays free.
                const kind: 'paid' | 'grant' = vacFunding === 'grant' ? 'grant' : 'paid';
                const endsAtMs = active.maxWashEndTime
                  ? new Date(active.maxWashEndTime).getTime()
                  : new Date(active.startedAt).getTime() + (active.claimedMinutes ?? 0) * 60000;
                if (endsAtMs > Date.now()) {
                  const totalMin = active.claimedMinutes
                    ?? Math.max(1, Math.round((endsAtMs - new Date(active.startedAt).getTime()) / 60000));
                  startVacuumCountdown(
                    active.tierName ?? (kind === 'grant' ? 'Free Vacuum' : 'Vacuum'),
                    totalMin,
                    active.amountCents ?? active.totalChargedCents ?? 0,
                    kind,
                    active.transactionId,
                    active.sessionId,
                    endsAtMs,
                  );
                }
              } else {
                // Count-up session still active — resume elapsed timer.
                // Membership/bundle vacuum sessions restore their funding
                // marker so the running UI shows the right cap + stop
                // warnings instead of a bogus dollar meter.
                setSessionId(active.sessionId);
                sessionIdRef.current = active.sessionId;
                setPresetMode(false);
                const funding = active.paymentMethod === 'subscription' ? 'membership'
                  : (active.paymentMethod === 'bundle' || active.purchasedBundleItemId != null) ? 'bundle'
                    : 'paid';
                if (funding === 'membership') {
                  setSessionIsSubscription(true);
                  setSessionDailyMinutesAllowed(active.dailyMinutesRemaining ?? active.claimedMinutes ?? null);
                  setSessionMonthlyMinutesAllowed(active.monthlyMinutesRemaining ?? null);
                  setSessionMinMinutes(active.minMinutes ?? 0);
                  subLimitHitRef.current = false;
                  setMinimumCharge(0);
                  setMaximumCharge(null);
                } else if (funding === 'bundle') {
                  setSessionIsBundle(true);
                  setSessionClaimedMinutes(active.claimedMinutes ?? null);
                  setSessionMinMinutes(active.minMinutes ?? 0);
                  setSessionAllAtOnce(!!active.allAtOnce);
                  subLimitHitRef.current = false;
                  setMinimumCharge(0);
                  setMaximumCharge(null);
                } else {
                  setMinimumCharge(active.minimumChargeCents || 300);
                  setMaximumCharge(active.maximumChargeCents || null);
                }
                setSessionStartTime(new Date(active.startedAt));
                setElapsedSeconds(Math.floor((Date.now() - new Date(active.startedAt).getTime()) / 1000));
                setSessionActive(true);
              }
            }
          } catch { /* non-fatal */ }
        })();
      }
      return () => {
        if (sessionIdRef.current && !presetModeRef.current && !prepaidSessionRef.current) {
          api('/api/wash-sessions/stop', {
            method: 'POST',
            body: { sessionId: sessionIdRef.current },
          }).catch(() => {});
          sessionIdRef.current = null;
        }
      };
    }, [bayId])
  );

  // Poll controller status during active wash (every 10s)
  const [midWashOffline, setMidWashOffline] = useState(false);
  useEffect(() => {
    if (!sessionActive || !bay?.controllerId) return;
    const poll = setInterval(async () => {
      try {
        const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
        setControllerStatus(fresh);
        setMidWashOffline(fresh.hasController && !fresh.controllerOnline);
      } catch { /* non-fatal */ }
    }, 10000);
    return () => clearInterval(poll);
  }, [sessionActive, bay?.controllerId, bayId]);

  // Block navigation while count-up wash is active (preset runs to completion, can navigate away)
  const navigation = useNavigation();
  useEffect(() => {
    if (!sessionActive || presetMode || sessionIsBundle || sessionIsSubscription) return;
    const unsubscribe = navigation.addListener('beforeRemove', (e: any) => {
      e.preventDefault();
      toast.show({ type: 'error', title: 'Wash In Progress', message: 'Stop your wash before leaving.' });
    });
    return unsubscribe;
  }, [navigation, sessionActive, presetMode, sessionIsBundle, sessionIsSubscription]);

  useEffect(() => {
    if (!sessionActive || !maximumCharge || !bay?.pricePerMinute) return;
    const rawCostCents = Math.round((elapsedSeconds / 60) * bay.pricePerMinute * 100);
    if (rawCostCents >= maximumCharge) {
      if (fleetSessionId) {
        handleFleetStopSession();
      } else {
        handleStopSession();
      }
    }
  }, [elapsedSeconds, sessionActive, maximumCharge]);

  // Check for payment method, redirect if none
  const ensurePaymentMethod = async (): Promise<boolean> => {
    if (role === 'admin') return true;
    if (!isAuthenticated) {
      toast.show({
        type: 'warning',
        title: 'Sign In Required',
        message: 'Please sign in to purchase a wash.',
        duration: 0,
        actions: [
          { label: 'Cancel', onPress: () => toast.hide(), style: 'cancel' },
          { label: 'Sign In', onPress: () => { toast.hide(); router.push('/login'); } },
        ],
      });
      return false;
    }

    try {
      const res = await api<{ hasPaymentMethods: boolean }>('/api/payments/has-methods');
      if (!res.hasPaymentMethods) {
        toast.show({
          type: 'warning',
          title: 'Payment Method Required',
          message: 'Please add a card before starting a wash.',
          duration: 0,
          actions: [
            { label: 'Cancel', onPress: () => toast.hide(), style: 'cancel' },
            {
              label: 'Add Card',
              onPress: () => {
                toast.hide();
                router.push('/payment-methods?returnTo=wash');
              },
            },
          ],
        });
        return false;
      }
      return true;
    } catch {
      return true; // Allow through on network error, backend will catch it
    }
  };

  // How a POST-PROMO amount splits between gift credit and the card (mirrors
  // the backend's draw math, including the 50¢ Stripe-minimum shift).
  const creditSplit = (amt: number) => {
    const avail = (!selectedFleetId && useWashCredit && (washCreditCents ?? 0) > 0) ? washCreditCents! : 0;
    let draw = Math.min(avail, amt);
    const rem = amt - draw;
    if (rem > 0 && rem < 50) draw = Math.max(0, amt - 50);
    return { draw, card: amt - draw };
  };

  // Promo value against a self-serve amount: free minutes knock their time's
  // value off, percent/free_wash reduce proportionally/entirely.
  const selfServeVoucherDiscount = (amt: number) => {
    if (!appliedVoucher || selectedFleetId) return 0;
    if (appliedVoucher.discountKind === 'free_minutes') {
      return Math.min(amt, Math.round((appliedVoucher.freeMinutes ?? 0) * (bay?.pricePerMinute ?? 0) * 100));
    }
    if (appliedVoucher.discountKind === 'percent') return Math.round(amt * Math.min(100, appliedVoucher.percentOff ?? 0) / 100);
    if (appliedVoucher.discountKind === 'free_wash') return amt;
    return 0;
  };

  // Start self-serve session (pre-authorizes max charge)
  const handleStartSession = async () => {
    // For full-gift / free-minutes voucher self-serve sessions, no card required.
    const voucherFullCoverage = appliedVoucher && (
      appliedVoucher.discountKind === 'free_wash' ||
      appliedVoucher.discountKind === 'free_minutes' ||
      (appliedVoucher.discountKind === 'percent' && appliedVoucher.percentOff === 100)
    );
    if (!voucherFullCoverage && !(await ensurePaymentMethod())) return;

    // Re-check controller status right before starting
    if (bay?.controllerId) {
      try {
        const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
        setControllerStatus(fresh);
        if (fresh.hasController && !fresh.controllerOnline) {
          routeToFailure('controller_offline', fresh.message || 'Cannot start wash — equipment is not responding.');
          return;
        }
      } catch { /* proceed anyway if status check itself fails */ }
    }

    setPurchasing(true);
    setStatusMessage(null);
    try {
      const body: any = { bayId: bay!.id };
      if (appliedVoucher) body.discountSource = { type: 'voucher', code: appliedVoucher.code };
      // Only send an explicit opt-OUT (visible toggle unticked). Otherwise omit
      // the field so the backend's default-ON checks the live balance — a tab
      // whose credit fetch raced or went stale can't silently disable the draw.
      // Promos and credit stack now, so this is independent of the voucher.
      if ((washCreditCents ?? 0) > 0 && !useWashCredit) body.useWashCredit = false;
      const res = await api<{
        sessionId: string; startedAt: string; minimumCharge: number;
        maximumCharge: number; preAuthorized: number;
      }>('/api/wash-sessions/start', {
        method: 'POST',
        body,
      });
      setSessionId(res.sessionId);
      sessionIdRef.current = res.sessionId;
      setSessionActive(true);
      setSessionStartTime(new Date(res.startedAt));
      setElapsedSeconds(0);
      setMinimumCharge(res.minimumCharge);
      setMaximumCharge(res.maximumCharge);
      setPreAuthorized(res.preAuthorized);
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      const reason = getFailureReason(err);
      if (reason === 'no_payment_method' || (err instanceof ApiError && err.code === 'NO_PAYMENT_METHOD') || err.message?.includes('No payment method')) {
        router.push('/payment-methods?returnTo=wash');
      } else {
        routeToFailure(reason, failMsg(err));
      }
    } finally {
      setPurchasing(false);
    }
  };

  // Purchase a fixed-amount self-serve wash (countdown timer). Timed packages
  // send their name so the backend runs the package's minutes (non-linear OK);
  // legacy presets and custom amounts send the amount alone.
  const handlePresetPurchase = async (amountCents: number, packageName?: string) => {
    if (!(await ensurePaymentMethod())) return;

    // Re-check controller
    if (bay?.controllerId) {
      try {
        const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
        setControllerStatus(fresh);
        if (fresh.hasController && !fresh.controllerOnline) {
          routeToFailure('controller_offline', fresh.message || 'Cannot start wash — equipment is not responding.');
          return;
        }
      } catch { /* proceed */ }
    }

    setPurchasing(true);
    setStatusMessage(null);
    try {
      const res = await api<{
        sessionId: string; startedAt: string; endsAt: string;
        amount: number; creditAppliedCents?: number; voucherDiscountCents?: number; pointsEarned?: number;
        durationMinutes: number; durationMs: number; mode: string;
      }>('/api/purchases/self-serve-preset', {
        method: 'POST',
        body: {
          bayId: bay!.id, amount: amountCents,
          ...(packageName ? { packageName } : {}),
          ...(appliedVoucher ? { discountSource: { type: 'voucher', code: appliedVoucher.code } } : {}),
          // Only an explicit opt-out is sent; omitting lets the backend's
          // default-ON apply against the live balance (stale-tab safe).
          ...((washCreditCents ?? 0) > 0 && !useWashCredit ? { useWashCredit: false } : {}),
        },
      });
      setSessionId(res.sessionId);
      sessionIdRef.current = res.sessionId;
      setSessionActive(true);
      setPresetMode(true);
      setPresetAmount(res.amount);
      setPresetCreditApplied(res.creditAppliedCents ?? 0);
      setPresetVoucherApplied(res.voucherDiscountCents ?? 0);
      setPresetPointsEarned(res.pointsEarned ?? 0);
      setPresetDurationMs(res.durationMs);
      setSessionStartTime(new Date(res.startedAt));
      setPresetEndsAt(new Date(res.endsAt));
      setRemainingSeconds(Math.ceil(res.durationMs / 1000));
      setConfirmStop(false);
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      const reason = getFailureReason(err);
      if (reason === 'no_payment_method' || (err instanceof ApiError && err.code === 'NO_PAYMENT_METHOD') || err.message?.includes('No payment method')) {
        router.push('/payment-methods?returnTo=wash');
      } else {
        routeToFailure(reason, failMsg(err));
      }
    } finally {
      setPurchasing(false);
    }
  };

  // Handle early stop on preset wash — needs confirmation
  const handlePresetStop = () => {
    if (!confirmStop) {
      setConfirmStop(true);
      return;
    }
    // User confirmed — send OFF command via stop endpoint
    const doStop = async () => {
      setPurchasing(true);
      try {
        await api('/api/wash-sessions/stop', {
          method: 'POST',
          body: { sessionId },
        });
        setSessionActive(false);
        sessionIdRef.current = null;
        setPresetMode(false);
        setWashComplete({
          washType: 'self-serve', washId: sessionId!,
          amount: presetAmount,
          status: 'completed',
          locationName: location?.name, bayName: bay?.name,
          transactionId: sessionId!,
          creditUsedCents: presetCreditApplied || undefined,
          promoUsedCents: presetVoucherApplied || undefined,
          pointsEarned: presetPointsEarned || undefined,
        });
      } catch (err: any) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      } finally {
        setPurchasing(false);
        setConfirmStop(false);
      }
    };
    doStop();
  };

  // Stop session (captures actual amount from pre-auth; membership/bundle
  // sessions charge nothing — the backend deducts minutes with the
  // minimum-minutes / allAtOnce rules applied).
  const handleStopSession = async () => {
    if (!sessionId) return;
    const wasBundle = sessionIsBundle;
    const wasSubscription = sessionIsSubscription;

    const doStop = async () => {
      setPurchasing(true);
      setSessionActive(false); // freeze timer immediately for instant feedback
      setTimerMessage(null);
      try {
        const res = await api<{
          totalChargedCents: number; durationMinutes: number;
          creditAppliedCents?: number; voucherDiscountCents?: number; pointsEarned?: number;
          message: string; paymentCaptured: boolean;
        }>('/api/wash-sessions/stop', {
          method: 'POST',
          body: { sessionId },
        });
        sessionIdRef.current = null;
        if (wasBundle) {
          // Unused minutes may have been refunded — refresh the remaining
          // balance for the bundle card behind the completion overlay.
          setSessionIsBundle(false);
          setSessionClaimedMinutes(null);
          setSessionAllAtOnce(false);
          setSessionMinMinutes(0);
          apiGet<{ hasBundles: boolean; bundles: VacBundleItem[] }>(`/api/bundles/check?bayId=${bayId}`)
            .then(bc => setVacBundles(bc.bundles ?? []))
            .catch(() => {});
        } else if (wasSubscription) {
          setSessionIsSubscription(false);
          setSessionMinMinutes(0);
          apiGet<any>(`/api/subscriptions/check?bayId=${bayId}`)
            .then(c => { if (c?.hasSubscription) setSubCheck(c); })
            .catch(() => {});
        }
        setWashComplete({
          washType: 'self-serve', washId: sessionId,
          amount: res.totalChargedCents,
          durationMinutes: res.durationMinutes,
          status: 'completed',
          locationName: location?.name, bayName: bay?.name,
          transactionId: sessionId,
          creditUsedCents: res.creditAppliedCents,
          promoUsedCents: res.voucherDiscountCents,
          pointsEarned: res.pointsEarned,
        });
      } catch (err: any) {
        setSessionActive(true); // re-enable on failure so the user can retry
        setTimerMessage({ type: 'error', text: err.message });
      } finally {
        setPurchasing(false);
      }
    };

    // Membership/bundle sessions: minute-based warnings instead of the
    // dollar-minimum double-tap.
    if (wasBundle || wasSubscription) {
      if (wasBundle && sessionAllAtOnce) {
        confirmDestructive(
          'Stop the vacuum?',
          'These minutes must be used in one session — stopping now forfeits the rest.',
          doStop,
          'Stop Anyway',
        );
        return;
      }
      const elapsedMin = Math.ceil(elapsedSeconds / 60);
      const effMin = Math.min(sessionMinMinutes, sessionClaimedMinutes ?? Infinity);
      if (effMin > 0 && elapsedMin < effMin) {
        confirmDestructive(
          'Stop early?',
          `Minimum is ${effMin} minute${effMin === 1 ? '' : 's'} — stopping now still uses ${effMin} min from your ${wasBundle ? 'bundle' : 'membership'}.`,
          doStop,
          'Stop Anyway',
        );
        return;
      }
      doStop();
      return;
    }

    if (minimumMet) {
      doStop();
    } else {
      if (timerMessage?.type === 'error' && timerMessage.text.startsWith('Minimum')) {
        doStop();
      } else {
        setTimerMessage({ type: 'error', text: `Minimum charge of $${(minimumCharge / 100).toFixed(2)} applies. Tap again to confirm.` });
      }
    }
  };

  // Purchase automatic wash (charges saved card immediately).
  // If a voucher is applied, sends discountSource={type:'voucher',code} —
  // 100%-gift vouchers skip Stripe entirely so we don't gate on a saved card.
  const handlePurchaseAutomatic = async () => {
    const isFullGift = voucherAppliesToSelectedTier && (
      appliedVoucher!.discountKind === 'free_wash'
      || (appliedVoucher!.discountKind === 'percent' && appliedVoucher!.percentOff === 100));
    // If wash credit covers the full sticker price, no card needed either.
    const selectedTierPriceLocal = bay?.tiers?.find(t => t.name === selectedTier)?.price ?? 0;
    const creditCoversAll = !voucherAppliesToSelectedTier && useWashCredit
      && washCreditCents != null && washCreditCents >= selectedTierPriceLocal;
    if (!isFullGift && !creditCoversAll && !(await ensurePaymentMethod())) return;
    if (!selectedTier) return;

    // Re-check controller status right before purchasing
    if (bay?.controllerId) {
      try {
        const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
        setControllerStatus(fresh);
        if (fresh.hasController && !fresh.controllerOnline) {
          routeToFailure('controller_offline', fresh.message || 'Cannot start wash — equipment is not responding.');
          return;
        }
      } catch { /* proceed anyway */ }
    }

    setPurchasing(true);
    setStatusMessage(null);
    try {
      const body: any = { bayId: bay!.id, tierName: selectedTier };
      if (selectedUpsells.length) body.upsellActionIds = selectedUpsells; // 1.4 P20
      if (voucherAppliesToSelectedTier) body.discountSource = { type: 'voucher', code: appliedVoucher!.code };
      // Credit stacks after the promo; only an explicit opt-out is sent so the
      // backend's default-ON checks the live balance (stale-tab safe).
      if ((washCreditCents ?? 0) > 0 && !useWashCredit) body.useWashCredit = false;
      const res = await api<{
        transactionId: string; amount: number; tierName: string; status: string;
        discount?: { source: string; amountCents: number };
        voucherDiscountCents?: number; creditAppliedCents?: number; pointsEarned?: number;
        grantedVacMinutes?: number | null;
        upsells?: { actionId: string; name: string; priceCents: number; included: boolean }[]; upsellCents?: number;
        queued?: boolean; programId?: string | null; queuedMessage?: string | null;
      }>('/api/purchases/automatic', {
        method: 'POST',
        body,
      });
      const complete = {
        washType: 'automatic' as const, washId: res.transactionId,
        tierName: res.tierName || selectedTier,
        amount: res.amount, status: res.status,
        locationName: location?.name, bayName: bay?.name,
        transactionId: res.transactionId,
        creditUsedCents: res.creditAppliedCents || undefined,
        promoUsedCents: res.voucherDiscountCents || undefined,
        pointsEarned: res.pointsEarned,
        // Tier perk (free vac minutes) — surfaces the "scan a vacuum" callout.
        grantedVacMinutes: res.grantedVacMinutes ?? null,
        lineItems: res.upsells?.length ? [{ name: res.tierName || selectedTier, amountCents: selectedTierPriceLocal, kind: 'tier' }, ...res.upsells.map(u => ({ name: u.name, amountCents: u.priceCents, included: u.included, kind: 'upsell' }))] : undefined,
      };
      setSelectedUpsells([]);
      if (res.queued && res.programId) setQueuedWait({ programId: res.programId, complete, message: res.queuedMessage ?? null });
      else setWashComplete(complete);
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      const reason = getFailureReason(err);
      if (reason === 'no_payment_method' || (err instanceof ApiError && err.code === 'NO_PAYMENT_METHOD') || err.message?.includes('No payment method')) {
        router.push('/payment-methods?returnTo=wash');
      } else {
        routeToFailure(reason, failMsg(err));
      }
    } finally {
      setPurchasing(false);
    }
  };

  // ─── Vacuum handlers ───

  // Client-side countdown for the running vacuum (no session polling exists).
  useEffect(() => {
    if (!vacRunning || vacDone) return;
    const iv = setInterval(() => {
      const rem = Math.max(0, Math.ceil((vacRunning.endsAt - Date.now()) / 1000));
      setVacRemainingSec(rem);
      if (rem <= 0) setVacDone(true);
    }, 1000);
    return () => clearInterval(iv);
  }, [vacRunning, vacDone]);

  const startVacuumCountdown = (
    tierName: string, minutes: number, amountCents: number,
    kind: 'paid' | 'grant', transactionId?: string,
    sessionId?: string, endsAtMs?: number,
  ) => {
    // Server sessions carry the authoritative maxWashEndTime; without one
    // (old backend) the countdown is client-computed as before.
    const endsAt = endsAtMs ?? Date.now() + minutes * 60000;
    setVacRunning({ tierName, minutes, endsAt, amountCents, kind, transactionId, sessionId });
    setVacRemainingSec(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
    setVacDone(false);
  };

  // Pre-start controller re-check, shared by all three vacuum start paths
  // (mirrors the wash handlers above).
  const vacuumControllerOk = async (): Promise<boolean> => {
    if (!bay?.controllerId) return true;
    try {
      const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
      setControllerStatus(fresh);
      if (fresh.hasController && !fresh.controllerOnline) {
        routeToFailure('controller_offline', fresh.message || 'Cannot start vacuum — equipment is not responding.');
        return false;
      }
    } catch { /* proceed anyway */ }
    return true;
  };

  // Promo value against a vacuum package (mirrors the backend's math in
  // payments.ts): percent discounts the sticker; free_minutes covers up to
  // the minutes' cash value at the package's per-minute rate. free_wash is
  // rejected at vacuums server-side and never offered here.
  const vacuumVoucherDiscount = (tier: { price: number; durationMs?: number }): number => {
    if (!appliedVoucher) return 0;
    if (appliedVoucher.discountKind === 'percent') {
      return Math.round(tier.price * Math.min(100, appliedVoucher.percentOff ?? 0) / 100);
    }
    if (appliedVoucher.discountKind === 'free_minutes') {
      const durMin = (tier.durationMs ?? 0) / 60000;
      const perMinCents = durMin > 0 ? tier.price / durMin : 0;
      return Math.min(tier.price, Math.round((appliedVoucher.freeMinutes ?? 0) * perMinCents));
    }
    return 0;
  };

  // Card pay — same endpoint and guard path as the automatic wash purchase.
  // Wash credit auto-draws server-side; an applied voucher goes through as
  // discountSource (percent or free_minutes — mig 0064).
  const handleVacuumPurchase = async () => {
    if (!selectedTier || !bay) return;
    const tier = bay.tiers?.find(t => t.name === selectedTier);
    if (!tier) return;
    const voucherDiscount = vacuumVoucherDiscount(tier);
    const afterVoucher = Math.max(0, tier.price - voucherDiscount);
    const voucherCoversAll = !!appliedVoucher && afterVoucher === 0;
    const creditCoversAll = useWashCredit && washCreditCents != null && washCreditCents >= afterVoucher;
    if (!voucherCoversAll && !creditCoversAll && !(await ensurePaymentMethod())) return;
    if (!(await vacuumControllerOk())) return;
    setPurchasing(true);
    setStatusMessage(null);
    try {
      const body: any = { bayId: bay.id, tierName: selectedTier };
      if (appliedVoucher) body.discountSource = { type: 'voucher', code: appliedVoucher.code };
      // Only an explicit opt-out is sent; omitting lets the backend's
      // default-ON apply against the live balance (stale-tab safe).
      if ((washCreditCents ?? 0) > 0 && !useWashCredit) body.useWashCredit = false;
      const res = await api<{
        transactionId: string; amount: number; tierName: string; status: string;
        voucherDiscountCents?: number; creditAppliedCents?: number;
        // Wave 2: the purchase creates a tracked wash_sessions row (funding
        // 'prepaid') — absent on older backends.
        sessionId?: string; startedAt?: string; maxWashEndTime?: string;
        claimedMinutes?: number;
      }>('/api/purchases/automatic', { method: 'POST', body });
      startVacuumCountdown(
        res.tierName || selectedTier,
        res.claimedMinutes ?? (tier.durationMs ?? 0) / 60000,
        res.amount,
        'paid',
        res.transactionId,
        res.sessionId,
        res.maxWashEndTime ? new Date(res.maxWashEndTime).getTime() : undefined,
      );
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      const reason = getFailureReason(err);
      if (reason === 'no_payment_method' || (err instanceof ApiError && err.code === 'NO_PAYMENT_METHOD') || err.message?.includes('No payment method')) {
        router.push('/payment-methods?returnTo=wash');
      } else {
        routeToFailure(reason, failMsg(err));
      }
    } finally {
      setPurchasing(false);
    }
  };

  // Redeem the customer's open free-vac grant ($0, grantMinutes long) via the
  // mode-agnostic endpoint — works on package AND metered vacuums.
  const handleVacuumGrantRedeem = async (grant: VacGrant) => {
    if (!bay) return;
    if (!(await vacuumControllerOk())) return;
    setPurchasing(true);
    setStatusMessage(null);
    try {
      const res = await api<{
        transactionId: string; grantMinutes: number; status: string;
        // Wave 2: grant redemption creates a tracked wash_sessions row
        // (funding 'grant') — absent on older backends.
        sessionId?: string; startedAt?: string; maxWashEndTime?: string;
        claimedMinutes?: number;
      }>('/api/vac-grants/redeem', {
        method: 'POST',
        body: { bayId: bay.id },
      });
      setVacGrants(prev => prev.filter(g => g.id !== grant.id));
      startVacuumCountdown(
        'Free Vacuum',
        res.claimedMinutes ?? res.grantMinutes ?? grant.minutesGranted,
        0,
        'grant',
        res.transactionId,
        res.sessionId,
        res.maxWashEndTime ? new Date(res.maxWashEndTime).getTime() : undefined,
      );
    } catch (err: any) {
      if (err instanceof ApiError && err.code === 'NO_VAC_GRANT') {
        setVacGrants([]);
        toast.show({ type: 'error', title: 'Free vacuum unavailable', message: 'This free vacuum has expired or was already used.' });
        return;
      }
      if (redirectIfVerifyRequired(err)) return;
      routeToFailure(getFailureReason(err), failMsg(err));
    } finally {
      setPurchasing(false);
    }
  };

  // Stop a running countdown vacuum (paid OR grant) early. Always warned
  // first — no refund of money or unused minutes either way. Tracked
  // sessions (wave 2) stop via the generic /api/wash-sessions/stop; legacy
  // grant runs without a sessionId fall back to /api/vac-grants/stop.
  const handleVacCountdownStop = async () => {
    if (!bay || vacStopping || !vacRunning) return;
    setVacStopping(true);
    try {
      if (vacRunning.sessionId) {
        const res = await api<{ refundedMinutes?: number; message?: string }>('/api/wash-sessions/stop', {
          method: 'POST',
          body: { sessionId: vacRunning.sessionId },
        });
        toast.show({
          type: 'success',
          title: 'Vacuum stopped',
          message: res?.message || 'Vacuum stopped — no refund for unused time.',
        });
      } else {
        await apiPost('/api/vac-grants/stop', { bayId: bay.id });
        toast.show({ type: 'success', title: 'Vacuum stopped' });
      }
    } catch (err: any) {
      // "Not found / already closed" = the server no longer sees a running
      // session (timed out or swept) — ending the countdown UI is correct
      // either way. Anything else keeps the countdown so the customer can
      // retry.
      const msg = (err?.message || '').toLowerCase();
      const alreadyClosed = msg.includes('no running free vacuum')
        || msg.includes('not found')
        || msg.includes('no active');
      if (!alreadyClosed) {
        toast.show({ type: 'error', title: 'Could not stop vacuum', message: err.message });
        setVacStopping(false);
        return;
      }
    }
    setVacStopping(false);
    setVacRunning(null);
    setVacDone(false);
  };

  // Start a LIVE vacuum session from a prepaid bundle's vac-minute balance
  // (session mode — no tierName/minutes: the backend claims the full remainder
  // for allAtOnce items, else min(remaining, bay max)) and drop straight into
  // the count-up running UI. Stopping refunds unused minutes server-side
  // (minimum-minutes rule applies).
  const handleVacuumBundleStart = async (b: VacBundleItem) => {
    if (!bay) return;
    if (!(await vacuumControllerOk())) return;
    setPurchasing(true);
    setStatusMessage(null);
    try {
      const res = await apiPost<{
        sessionId: string; startedAt: string; maxWashEndTime: string;
        claimedMinutes: number; minMinutes: number; allAtOnce: boolean;
        remainingMinutes: number; status: string;
      }>('/api/bundles/authorize-vacuum', {
        purchasedBundleId: b.purchasedBundleId,
        bayId: bay.id,
      });
      setSessionId(res.sessionId);
      sessionIdRef.current = res.sessionId;
      setPresetMode(false);
      setSessionIsSubscription(false);
      setSessionIsBundle(true);
      setSessionClaimedMinutes(res.claimedMinutes);
      setSessionMinMinutes(res.minMinutes ?? 0);
      setSessionAllAtOnce(!!res.allAtOnce);
      subLimitHitRef.current = false;
      setSessionStartTime(new Date(res.startedAt));
      setElapsedSeconds(0);
      setMinimumCharge(0);
      setMaximumCharge(null);
      setPreAuthorized(null);
      setSessionActive(true);
      // Reflect the claim immediately — the stop refund (if any) comes back
      // via the bundles/check refresh after the session ends.
      setVacBundles(prev => prev.map(x => x.purchasedBundleId === b.purchasedBundleId
        ? { ...x, remainingMinutes: res.remainingMinutes }
        : x));
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      routeToFailure(getFailureReason(err), failMsg(err));
    } finally {
      setPurchasing(false);
    }
  };

  // Fleet purchase handler (automatic + preset self-serve)
  const handleFleetPurchase = async (washType: string, retailPrice: number) => {
    if (!selectedFleetId) return;
    setPurchasing(true);
    setStatusMessage(null);
    try {
      const res = await apiPost<{
        transactionId: string; sessionId?: string; retailPrice: number; status: string;
        startedAt?: string; endsAt?: string; durationMs?: number; amount?: number; mode?: string;
      }>(
        '/api/fleet/me/purchase-wash',
        { fleetAccountId: selectedFleetId, bayId: bay!.id, washType, retailPrice, ...(selectedUpsells.length ? { upsellActionIds: selectedUpsells } : {}) } // 1.4 P20
      );

      // If preset self-serve, show countdown timer
      if (res.mode === 'preset' && res.startedAt && res.endsAt && res.durationMs) {
        const sid = res.sessionId || res.transactionId; // sessionId from washSession; fallback for compat
        setSessionId(sid);
        sessionIdRef.current = sid;
        setSessionActive(true);
        setPresetMode(true);
        setPresetAmount(res.amount || res.retailPrice);
        setPresetDurationMs(res.durationMs);
        setSessionStartTime(new Date(res.startedAt));
        setPresetEndsAt(new Date(res.endsAt));
        setRemainingSeconds(Math.ceil(res.durationMs / 1000));
        setConfirmStop(false);
        setFleetSessionId(sid);
      } else {
        // Automatic wash — show success page
        const fleetName = fleetAccounts.find(f => f.accountId === selectedFleetId)?.companyName || 'fleet';
        setWashComplete({
          washType: 'automatic', washId: res.transactionId,
          tierName: washType, amount: res.retailPrice,
          status: 'completed', fleetName,
          locationName: location?.name, bayName: bay?.name,
          transactionId: res.transactionId,
        });
      }
    } catch (err: any) {
      routeToFailure(getFailureReason(err), failMsg(err));
    } finally {
      setPurchasing(false);
    }
  };

  // Fleet count-up start handler
  const handleFleetStartSession = async () => {
    if (!selectedFleetId) return;
    if (bay?.controllerId) {
      try {
        const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
        setControllerStatus(fresh);
        if (fresh.hasController && !fresh.controllerOnline) {
          routeToFailure('controller_offline', fresh.message || 'Cannot start wash — equipment is not responding.');
          return;
        }
      } catch { /* proceed */ }
    }

    setPurchasing(true);
    setStatusMessage(null);
    try {
      const res = await apiPost<{
        sessionId: string; startedAt: string; pricePerMinute: number;
        minimumCharge: number; maximumCharge: number | null; preAuthorized: number | null;
        fleetAccountId: string; fleetName: string;
      }>('/api/fleet/me/start-wash', { fleetAccountId: selectedFleetId, bayId: bay!.id });

      setSessionId(res.sessionId);
      sessionIdRef.current = res.sessionId;
      setSessionActive(true);
      setSessionStartTime(new Date(res.startedAt));
      setElapsedSeconds(0);
      setMinimumCharge(res.minimumCharge);
      setMaximumCharge(res.maximumCharge);
      setPreAuthorized(null);
      setFleetSessionId(res.sessionId);
    } catch (err: any) {
      routeToFailure(getFailureReason(err), failMsg(err));
    } finally {
      setPurchasing(false);
    }
  };

  // Fleet count-up stop handler
  const handleFleetStopSession = async () => {
    if (!fleetSessionId || !selectedFleetId) return;

    const doStop = async () => {
      setPurchasing(true);
      setSessionActive(false); // freeze timer immediately for instant feedback
      setTimerMessage(null);
      try {
        const res = await apiPost<{
          totalChargedCents: number; durationMinutes: number;
          message: string; paymentCaptured: boolean;
        }>('/api/fleet/me/stop-wash', { sessionId: fleetSessionId, fleetAccountId: selectedFleetId });
        sessionIdRef.current = null;
        const fleetName = fleetAccounts.find(f => f.accountId === selectedFleetId)?.companyName || 'fleet';
        setWashComplete({
          washType: 'self-serve', washId: fleetSessionId!,
          amount: res.totalChargedCents,
          durationMinutes: res.durationMinutes,
          fleetName,
          status: 'completed',
          locationName: location?.name, bayName: bay?.name,
          transactionId: fleetSessionId!,
        });
        setFleetSessionId(null);
      } catch (err: any) {
        setSessionActive(true); // re-enable on failure so the user can retry
        setTimerMessage({ type: 'error', text: err.message });
      } finally {
        setPurchasing(false);
      }
    };

    if (minimumMet) {
      doStop();
    } else {
      if (timerMessage?.type === 'error' && timerMessage.text.startsWith('Minimum')) {
        doStop();
      } else {
        setTimerMessage({ type: 'error', text: `Minimum charge of $${(minimumCharge / 100).toFixed(2)} applies. Tap again to confirm.` });
      }
    }
  };

  // Membership redemption gate: every membership wash first confirms the
  // registered vehicle is present ("You are redeeming for: …"). Members
  // without a vehicle profile yet (signed up before mig 0055, or via an old
  // app build) are prompted to create one and can't redeem until they do.
  const startMembershipRedemption = (kind: 'automatic' | 'selfserve') => {
    if (!subCheck?.canWash) return;
    if (!subCheck.vehicle) {
      setPendingRedeemKind(kind);
      setVehicleModalOpen(true);
      return;
    }
    setRedeemConfirm(kind);
  };

  // Save handler for the vehicle modal — creates/updates then resumes any
  // pending redemption at the confirmation step.
  const handleVehicleSave = async (fields: {
    licensePlate: string; make: string; model: string; color: string; year: number | null;
  }) => {
    const subId = subCheck?.subscriptionId ?? purchasedSub?.id;
    if (!subId) return;
    const res = await api<{ vehicle: SubscriptionVehicle }>(
      `/api/subscriptions/${subId}/vehicle`,
      { method: 'PUT', body: fields },
    );
    setSubCheck(prev => (prev ? { ...prev, vehicle: res.vehicle } : prev));
    setPurchasedSub(prev => (prev ? { ...prev, vehicle: res.vehicle } : prev));
    setVehicleModalOpen(false);
    if (pendingRedeemKind) {
      setRedeemConfirm(pendingRedeemKind);
      setPendingRedeemKind(null);
    }
  };

  // Subscription membership wash handler (after vehicle confirmation)
  const handleSubscriptionWash = async () => {
    if (!subCheck?.canWash) return;

    // Re-check controller status
    if (bay?.controllerId) {
      try {
        const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
        setControllerStatus(fresh);
        if (fresh.hasController && !fresh.controllerOnline) {
          routeToFailure('controller_offline', fresh.message || 'Cannot start wash — equipment is not responding.');
          return;
        }
      } catch { /* proceed */ }
    }

    setPurchasing(true);
    setStatusMessage(null);
    try {
      const res = await apiPost<{
        washId: string; tierName: string; washesUsedToday: number;
        washesUsedThisMonth: number; maxWashesPerDay: number; maxWashesPerMonth: number;
        upsells?: { actionId: string; name: string; priceCents: number; included: boolean }[]; upsellChargeCents?: number;
        queued?: boolean; programId?: string | null; queuedMessage?: string | null;
      }>('/api/subscriptions/authorize-wash', { bayId: bay!.id, vehicleConfirmed: true, ...(selectedUpsells.length ? { upsellActionIds: selectedUpsells } : {}) });

      setSubCheck(prev => prev ? {
        ...prev,
        washesUsedToday: res.washesUsedToday,
        washesUsedThisMonth: res.washesUsedThisMonth,
        dailyRemaining: res.maxWashesPerDay === 0 ? null : res.maxWashesPerDay - res.washesUsedToday,
        monthlyRemaining: res.maxWashesPerMonth === 0 ? null : res.maxWashesPerMonth - res.washesUsedThisMonth,
        canWash: (res.maxWashesPerDay === 0 || res.maxWashesPerDay - res.washesUsedToday > 0)
          && (res.maxWashesPerMonth === 0 || res.maxWashesPerMonth - res.washesUsedThisMonth > 0),
      } : null);

      const complete = {
        washType: 'subscription' as const, washId: res.washId,
        tierName: res.tierName, planName: subCheck.planName,
        washesUsedToday: res.washesUsedToday,
        maxWashesPerDay: res.maxWashesPerDay,
        washesUsedThisMonth: res.washesUsedThisMonth,
        maxWashesPerMonth: res.maxWashesPerMonth,
        locationName: location?.name, bayName: bay?.name,
        transactionId: res.washId,
        amount: res.upsellChargeCents || undefined,
        lineItems: res.upsells?.length ? [{ name: res.tierName, amountCents: 0, included: true, kind: 'tier' }, ...res.upsells.map(u => ({ name: u.name, amountCents: u.priceCents, included: u.included, kind: 'upsell' }))] : undefined,
      };
      setSelectedUpsells([]);
      if (res.queued && res.programId) setQueuedWait({ programId: res.programId, complete, message: res.queuedMessage ?? null });
      else setWashComplete(complete);
    } catch (err: any) {
      // Backend says a vehicle profile is required (e.g. deleted server-side
      // since our check) — collect it instead of dead-ending on failure.
      if (err instanceof ApiError && err.code === 'VEHICLE_REQUIRED') {
        setSubCheck(prev => (prev ? { ...prev, vehicle: null } : prev));
        setPendingRedeemKind('automatic');
        setVehicleModalOpen(true);
        return;
      }
      routeToFailure(getFailureReason(err), failMsg(err));
    } finally {
      setPurchasing(false);
    }
  };

  // Subscription self-serve session start — no Stripe pre-auth; tick job deducts minutes live.
  const handleSubscriptionSelfServeStart = async () => {
    if (!subCheck?.canWash) return;
    if (bay?.controllerId) {
      try {
        const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
        setControllerStatus(fresh);
        if (fresh.hasController && !fresh.controllerOnline) {
          routeToFailure('controller_offline', fresh.message || 'Cannot start wash — equipment is not responding.');
          return;
        }
      } catch { /* proceed */ }
    }
    setPurchasing(true);
    setStatusMessage(null);
    try {
      const res = await api<{
        sessionId: string; startedAt: string; maxSessionMinutes: number;
        dailyMinutesRemaining: number; monthlyMinutesRemaining: number;
        // Effective minimum minutes (0 when plan.overrideMinimums) — drives
        // the stop-early warning; absent on older backends → no warning.
        minMinutes?: number; effectiveMinMinutes?: number;
      }>('/api/wash-sessions/start-subscription', {
        method: 'POST',
        body: { bayId: bay!.id, vehicleConfirmed: true },
      });
      setSessionId(res.sessionId);
      sessionIdRef.current = res.sessionId;
      setSessionActive(true);
      setSessionIsSubscription(true);
      setSessionIsBundle(false);
      setSessionDailyMinutesAllowed(res.dailyMinutesRemaining);
      setSessionMonthlyMinutesAllowed(res.monthlyMinutesRemaining);
      setSessionMinMinutes(res.effectiveMinMinutes ?? res.minMinutes ?? 0);
      subLimitHitRef.current = false;
      setSessionStartTime(new Date(res.startedAt));
      setElapsedSeconds(0);
      setMinimumCharge(0);
      setMaximumCharge(null);
      setPreAuthorized(0);
    } catch (err: any) {
      if (err instanceof ApiError && err.code === 'VEHICLE_REQUIRED') {
        setSubCheck(prev => (prev ? { ...prev, vehicle: null } : prev));
        setPendingRedeemKind('selfserve');
        setVehicleModalOpen(true);
        return;
      }
      routeToFailure(getFailureReason(err), failMsg(err));
    } finally {
      setPurchasing(false);
    }
  };

  // Subscribe to a plan from the wash screen
  // Once a freshly-bought bundle's "use" section has rendered (and recorded its
  // offset via onLayout), scroll it into view so the customer can use it now.
  useEffect(() => {
    if (!pendingBundleFocus) return;
    const t = setTimeout(() => {
      scrollRef.current?.scrollTo({ y: Math.max(0, bundleUseY.current - 12), animated: true });
      setPendingBundleFocus(false);
    }, 250);
    return () => clearTimeout(t);
  }, [pendingBundleFocus, bundleCheck]);

  const handleSubscribe = async (planId: string) => {
    if (!isAuthenticated) {
      toast.show({
        type: 'warning', title: 'Sign In Required',
        message: 'Please sign in to subscribe.',
        duration: 0,
        actions: [
          { label: 'Cancel', onPress: () => toast.hide(), style: 'cancel' },
          { label: 'Sign In', onPress: () => { toast.hide(); router.push('/login'); } },
        ],
      });
      return;
    }
    try {
      const res = await api<{ hasPaymentMethods: boolean }>('/api/payments/has-methods');
      if (!res.hasPaymentMethods) {
        toast.show({
          type: 'warning', title: 'Payment Method Required',
          message: 'Please add a card before subscribing.',
          duration: 0,
          actions: [
            { label: 'Cancel', onPress: () => toast.hide(), style: 'cancel' },
            { label: 'Add Card', onPress: () => { toast.hide(); router.push('/payment-methods?returnTo=wash'); } },
          ],
        });
        return;
      }
    } catch { /* allow through */ }

    // Positive confirmation before the recurring charge starts.
    const plan = availablePlans.find(p => p.id === planId);
    const priceStr = plan ? `$${(plan.priceMonthly / 100).toFixed(2)}/mo` : '';
    confirmChoice(
      plan ? `Start your ${plan.name} membership?` : 'Start this membership?',
      plan
        ? `You'll be charged ${priceStr}, renewing monthly. Valid for a single registered vehicle. Cancel anytime from your account.`
        : 'Confirm your new membership.',
      () => performSubscribe(planId),
      'Yes, start membership',
    );
  };

  const performSubscribe = async (planId: string) => {
    const plan = availablePlans.find(p => p.id === planId);
    setSubscribing(planId);
    try {
      const subRes = await apiPost<{ id: string; vehicle: SubscriptionVehicle | null }>(
        '/api/subscriptions/subscribe', { planId });
      // vehicle is undefined from pre-mig-0055 backends — treat as null.
      setPurchasedSub({ id: subRes.id, vehicle: subRes.vehicle ?? null });
      // Prominent modal replaces the old toast — an unmissable "you bought this"
      // so nobody subscribes twice by accident.
      setPurchaseSuccess({
        kind: 'subscription',
        headline: 'Membership Active!',
        detail: plan
          ? `${plan.name} Membership — $${(plan.priceMonthly / 100).toFixed(2)}/mo`
          : 'Your new membership',
        readyLine: 'Your membership is ready below — included washes start now. It renews monthly; cancel anytime from your account.',
      });
      // Refresh subscription check
      setAvailablePlans([]);
      try {
        const check = await apiGet<any>(`/api/subscriptions/check?bayId=${bayId}`);
        if (check.hasSubscription) setSubCheck(check);
        else setOtherSub(check.otherSubscription ?? null);
      } catch { /* ok */ }
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      const reason = getFailureReason(err);
      if (reason === 'no_payment_method' || (err instanceof ApiError && err.code === 'NO_PAYMENT_METHOD') || err.message?.includes('No payment method')) {
        router.push('/payment-methods?returnTo=wash');
      } else {
        routeToFailure(reason, failMsg(err));
      }
    } finally {
      setSubscribing(null);
    }
  };

  // Bundle wash handler
  const handleBundleWash = async (purchasedBundleId: string) => {
    // Re-check controller status
    if (bay?.controllerId) {
      try {
        const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
        setControllerStatus(fresh);
        if (fresh.hasController && !fresh.controllerOnline) {
          routeToFailure('controller_offline', fresh.message || 'Cannot start wash — equipment is not responding.');
          return;
        }
      } catch { /* proceed */ }
    }

    setPurchasing(true);
    setStatusMessage(null);
    try {
      const res = await apiPost<{
        washId: string; tierName: string;
        washesRemaining: number; washesUsed: number; washesTotal: number;
        status: string;
        upsells?: { actionId: string; name: string; priceCents: number; included: boolean }[]; upsellChargeCents?: number;
        queued?: boolean; programId?: string | null; queuedMessage?: string | null;
      }>('/api/bundles/authorize-wash', { purchasedBundleId, bayId: bay!.id, ...(selectedUpsells.length ? { upsellActionIds: selectedUpsells } : {}) });

      // Update bundle check state
      setBundleCheck(prev => {
        if (!prev) return prev;
        return {
          hasBundles: prev.bundles.some(b => b.purchasedBundleId !== purchasedBundleId || res.washesRemaining > 0),
          bundles: prev.bundles.map(b =>
            b.purchasedBundleId === purchasedBundleId
              ? { ...b, washesRemaining: res.washesRemaining, washesUsed: res.washesUsed }
              : b
          ).filter(b => b.washesRemaining > 0),
        };
      });

      const bundleItem = bundleCheck?.bundles.find(b => b.purchasedBundleId === purchasedBundleId);
      const complete = {
        washType: 'bundle', washId: res.washId,
        tierName: res.tierName, planName: bundleItem?.planName || 'Bundle',
        washesRemaining: res.washesRemaining,
        washesTotal: res.washesTotal,
        washesUsed: res.washesUsed,
        locationName: location?.name, bayName: bay?.name,
        transactionId: res.washId,
              amount: res.upsellChargeCents || undefined,
        lineItems: res.upsells?.length ? [{ name: res.tierName, amountCents: 0, included: true, kind: 'tier' }, ...res.upsells.map(u => ({ name: u.name, amountCents: u.priceCents, included: u.included, kind: 'upsell' }))] : undefined,
      };
      setSelectedUpsells([]);
      if (res.queued && res.programId) setQueuedWait({ programId: res.programId, complete: complete as any, message: res.queuedMessage ?? null });
      else setWashComplete(complete as any);
    } catch (err: any) {
      routeToFailure(getFailureReason(err), failMsg(err));
    } finally {
      setPurchasing(false);
    }
  };

  // Bundle purchase handler
  const handleBundlePurchase = async (bundlePlanId: string) => {
    if (!isAuthenticated) {
      toast.show({
        type: 'warning', title: 'Sign In Required',
        message: 'Please sign in to purchase a bundle.',
        duration: 0,
        actions: [
          { label: 'Cancel', onPress: () => toast.hide(), style: 'cancel' },
          { label: 'Sign In', onPress: () => { toast.hide(); router.push('/login'); } },
        ],
      });
      return;
    }
    try {
      const res = await api<{ hasPaymentMethods: boolean }>('/api/payments/has-methods');
      if (!res.hasPaymentMethods) {
        toast.show({
          type: 'warning', title: 'Payment Method Required',
          message: 'Please add a card before purchasing.',
          duration: 0,
          actions: [
            { label: 'Cancel', onPress: () => toast.hide(), style: 'cancel' },
            { label: 'Add Card', onPress: () => { toast.hide(); router.push('/payment-methods?returnTo=wash'); } },
          ],
        });
        return;
      }
    } catch { /* allow through */ }

    // Positive confirmation before the one-time charge. The purchaseToken is
    // minted when this dialog opens: a retry of the SAME confirmed purchase
    // (double-tap, flaky network) reuses the token and the backend returns the
    // original purchase instead of charging again.
    const ab = availableBundles.find(b => b.id === bundlePlanId);
    const tierLabel = ab ? getTierLabel(ab.tierName, bay?.tiers) : 'wash';
    const priceStr = ab ? `$${(ab.bundlePrice / 100).toFixed(2)}` : '';
    const itemsSummary = ab ? bundleItemsSummary(ab.items) : null;
    const contents = itemsSummary ?? (ab ? `${ab.washCount} ${tierLabel} washes` : '');
    const expiryNote = ab?.expirationMinutes
      ? `, valid for ${ab.expirationMinutes} minutes after purchase`
      : ab?.expirationDays ? `, valid for ${ab.expirationDays} days` : '';
    const purchaseToken = makePurchaseToken();
    confirmChoice(
      ab ? `Get the ${ab.name}?` : 'Get this bundle?',
      ab
        ? `${contents} for ${priceStr} — yours to use anytime${expiryNote}.`
        : 'Confirm your bundle purchase.',
      () => performBundlePurchase(bundlePlanId, purchaseToken),
      'Yes, buy bundle',
    );
  };

  const performBundlePurchase = async (bundlePlanId: string, purchaseToken: string, confirmDuplicate = false) => {
    setPurchasingBundle(bundlePlanId);
    try {
      await apiPost('/api/bundles/purchase', {
        bundlePlanId,
        purchaseToken,
        ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
      });
      const ab = availableBundles.find(b => b.id === bundlePlanId);
      const tierLabel = ab ? getTierLabel(ab.tierName, bay?.tiers) : 'wash';
      const itemsSummary = ab ? bundleItemsSummary(ab.items) : null;
      // Prominent modal replaces the old toast — an unmissable "you bought this"
      // so nobody buys the same pack twice by accident.
      setPurchaseSuccess({
        kind: 'bundle',
        headline: 'Purchase Complete!',
        detail: ab
          ? `${ab.name} — ${itemsSummary ?? `${ab.washCount} ${tierLabel} washes`} for $${(ab.bundlePrice / 100).toFixed(2)}`
          : 'Your bundle',
        readyLine: 'Your prepaid bundle is ready below — use it anytime.',
      });
      // Refresh owned bundles so the "use a prepaid" section appears, then
      // scroll to it. We deliberately keep availableBundles populated so the
      // customer can still buy additional / different packs.
      try {
        if (bay?.type === 'vacuum') {
          const bc = await apiGet<{ hasBundles: boolean; bundles: VacBundleItem[] }>(`/api/bundles/check?bayId=${bayId}`);
          setVacBundles(bc.bundles ?? []);
        } else {
          const bCheck = await apiGet<{ hasBundles: boolean; bundles: BundleCheckItem[] }>(`/api/bundles/check?bayId=${bayId}`);
          setBundleCheck(bCheck);
          if (bCheck.hasBundles) setPendingBundleFocus(true);
        }
      } catch { /* ok */ }
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      // Same plan bought again within the backend's 2-minute window: require an
      // explicit second confirm, then resend with confirmDuplicate + a FRESH
      // token (reusing the old one would just return the original purchase).
      if (err instanceof ApiError && err.code === 'DUPLICATE_RECENT') {
        confirmChoice(
          'Buy this pack again?',
          'You bought this same wash pack just minutes ago — it may already be in your prepaid packs above. Do you want to buy another one?',
          () => performBundlePurchase(bundlePlanId, makePurchaseToken(), true),
          'Yes, buy another',
        );
        return;
      }
      const reason = getFailureReason(err);
      if (reason === 'no_payment_method' || (err instanceof ApiError && err.code === 'NO_PAYMENT_METHOD') || err.message?.includes('No payment method')) {
        router.push('/payment-methods?returnTo=wash');
      } else {
        routeToFailure(reason, failMsg(err));
      }
    } finally {
      setPurchasingBundle(null);
    }
  };

  const formatTime = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const rawCostCents = bay?.pricePerMinute
    ? Math.round((elapsedSeconds / 60) * bay.pricePerMinute * 100)
    : 0;
  const minimumMet = rawCostCents >= minimumCharge;
  let currentCostCents = minimumMet ? rawCostCents : 0;
  if (maximumCharge && currentCostCents > maximumCharge) currentCostCents = maximumCharge;
  const currentCostDisplay = (currentCostCents / 100).toFixed(2);
  const maxChargeNear = maximumCharge && rawCostCents >= maximumCharge * 0.8;

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <Stack.Screen options={{ title: 'Loading...', headerLeft: homeBackButton }} />
        <Ionicons name="water" size={48} color={colors.primary} />
        <Text style={styles.loadingText}>Loading wash info...</Text>
      </View>
    );
  }

  // Bay deactivated — handled by useEffect redirect below

  // Location not accepting payments — handled by useEffect redirect below

  // No controller configured
  if (bay && !bay.controllerId) {
    return (
      <>
        <Stack.Screen options={{ title: location?.name || 'Bay Unavailable', headerLeft: homeBackButton }} />
        <View style={styles.inactiveContainer}>
          <View style={[styles.inactiveIcon, { backgroundColor: '#fee2e2' }]}>
            <Ionicons name="hardware-chip-outline" size={64} color={colors.danger} />
          </View>
          <Text style={styles.inactiveTitle}>Not Set Up</Text>
          <Text style={styles.inactiveSubtext}>
            {bay.name} at {location?.name || 'this location'} does not have a controller configured yet.
          </Text>
          <Text style={styles.inactiveHint}>
            Please contact the location owner. This bay cannot accept app payments until a controller is installed.
          </Text>
          <TouchableOpacity style={styles.inactiveBtn} onPress={() => router.back()} hitSlop={8}>
            <Ionicons name="arrow-back" size={24} color={colors.primary} />
            <Text style={styles.inactiveBtnText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </>
    );
  }

  // Controller offline on initial load — full page block
  if (bay && bay.controllerId && controllerStatus?.hasController &&
      !controllerStatus.controllerOnline && !sessionActive) {
    return (
      <>
        <Stack.Screen options={{ title: location?.name || 'Bay Offline' }} />
        <View style={styles.inactiveContainer}>
          <View style={[styles.inactiveIcon, { backgroundColor: '#fef3c7' }]}>
            <Ionicons name="wifi-outline" size={64} color="#d97706" />
          </View>
          <Text style={styles.inactiveTitle}>Bay Offline</Text>
          <Text style={styles.inactiveSubtext}>
            {bay.name} at {location?.name} is not responding.
          </Text>
          <Text style={styles.inactiveHint}>
            The equipment may be powered off or experiencing connectivity issues.
          </Text>
          <TouchableOpacity
            style={styles.inactiveBtn}
            disabled={controllerCheckLoading}
            onPress={async () => {
              setControllerCheckLoading(true);
              try {
                const fresh = await api<any>(`/api/bays/${bayId}/controller-status`);
                setControllerStatus(fresh);
              } catch (err) { console.error('controller status check:', err); } finally {
                setControllerCheckLoading(false);
              }
            }}
          >
            <Ionicons name="refresh" size={24} color={colors.primary} />
            <Text style={styles.inactiveBtnText}>
              {controllerCheckLoading ? 'Checking...' : 'Try Again'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => router.back()} style={{ marginTop: spacing.sm }}>
            <Text style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </>
    );
  }

  // Active PRESET session — countdown timer (self-serve bays + metered vacuums)
  if (sessionActive && presetMode && isSessionBay) {
    const totalDurationSec = Math.ceil(presetDurationMs / 1000);
    const progress = totalDurationSec > 0 ? (totalDurationSec - remainingSeconds) / totalDurationSec : 0;
    const minutesLeft = Math.floor(remainingSeconds / 60);
    const secondsLeft = remainingSeconds % 60;

    return (
      <>
        <Stack.Screen options={{ title: isMeteredVac ? 'Vacuuming...' : 'Washing...' }} />
        <ScrollView style={{ flex: 1, backgroundColor: colors.background }} contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.lg }}>
          <View style={[styles.timerPulse, { backgroundColor: '#e0f7fa' }]}>
            <Ionicons name={isMeteredVac ? 'sparkles' : 'water'} size={28} color={colors.primary} />
          </View>

          <Text style={styles.timerLabel}>{isMeteredVac ? 'VACUUM IN PROGRESS' : 'WASH IN PROGRESS'}</Text>

          {midWashOffline && (
            <View style={styles.midWashWarning}>
              <Ionicons name="wifi" size={16} color="#d97706" />
              <Text style={styles.midWashWarningText}>
                Equipment connectivity lost. Your {sessionNoun} will continue for the remaining time.
              </Text>
            </View>
          )}

          <Text style={styles.timerDisplay}>
            {minutesLeft.toString().padStart(2, '0')}:{secondsLeft.toString().padStart(2, '0')}
          </Text>
          <Text style={styles.timerSubtext}>remaining</Text>

          {/* Progress bar */}
          <View style={styles.progressBarContainer}>
            <View style={[styles.progressBarFill, { width: `${Math.min(progress * 100, 100)}%` }]} />
          </View>

          <View style={[styles.costDisplay, styles.costDisplayMet]}>
            <Text style={styles.costLabel}>Prepaid</Text>
            <Text style={styles.costAmount}>${(presetAmount / 100).toFixed(2)}</Text>
            <Text style={styles.costRate}>
              {(presetDurationMs / 60000).toFixed(2)} min @ ${bay.pricePerMinute?.toFixed(2)}/min
            </Text>
          </View>

          <View style={styles.washInfo}>
            <Text style={styles.washInfoText}>{location?.name}</Text>
            <Text style={styles.washInfoText}>{bay.name}</Text>
          </View>

          <Text style={{ fontSize: 12, color: colors.textLight, textAlign: 'center', marginTop: spacing.md, maxWidth: 260 }}>
            {isMeteredVac ? 'The vacuum' : 'Wash'} will automatically end when the timer reaches zero.
          </Text>
        </ScrollView>

        {washComplete && (
          <WashCompleteOverlay
            data={washComplete}
            onDone={() => { setWashComplete(null); router.replace('/'); }}
            onReportProblem={() => { setReportTransactionId(washComplete.transactionId || washComplete.washId); setShowReport(true); }}
          />
        )}
        <ReportProblemModal
          visible={showReport}
          onClose={() => setShowReport(false)}
          transactionId={reportTransactionId}
          locationName={location?.name}
          bayName={bay?.name}
          amount={washComplete?.amount}
          type={bay?.type}
          tierName={washComplete?.tierName}
        />
      </>
    );
  }

  // Active COUNT-UP session — timer screen (self-serve bays + metered vacuums)
  if (sessionActive && isSessionBay) {
    return (
      <>
        <Stack.Screen options={{ title: isMeteredVac ? 'Vacuuming...' : 'Washing...', headerBackVisible: false, headerLeft: () => null, gestureEnabled: false }} />
        <ScrollView style={{ flex: 1, backgroundColor: colors.background }} contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.lg }}>
          <View style={styles.timerPulse}>
            <Ionicons name={isMeteredVac ? 'sparkles' : 'water'} size={28} color={colors.primary} />
          </View>

          <Text style={styles.timerLabel}>{isMeteredVac ? 'VACUUM IN PROGRESS' : 'WASH IN PROGRESS'}</Text>

          {midWashOffline && (
            <View style={styles.midWashWarning}>
              <Ionicons name="wifi" size={16} color="#d97706" />
              <Text style={styles.midWashWarningText}>
                Equipment connectivity lost. Your {sessionNoun} will continue for the remaining time.
              </Text>
            </View>
          )}

          <Text style={styles.timerDisplay}>{formatTime(elapsedSeconds)}</Text>
          <Text style={styles.timerSubtext}>elapsed</Text>

          {sessionIsBundle && sessionClaimedMinutes != null ? (
            <View style={[styles.costDisplay, styles.costDisplayMet]}>
              <Text style={styles.costLabel}>Bundle time remaining</Text>
              <Text style={styles.costAmount}>
                {formatTime(Math.max(0, Math.round(sessionClaimedMinutes * 60 - elapsedSeconds)))}
              </Text>
              <Text style={styles.costRate}>of {sessionClaimedMinutes} min from your bundle</Text>
              {sessionAllAtOnce ? (
                <Text style={styles.costRate}>Must be used in one session — stopping early forfeits the rest</Text>
              ) : sessionMinMinutes > 0 ? (
                <Text style={styles.costRate}>Minimum {Math.min(sessionMinMinutes, sessionClaimedMinutes)} min per session</Text>
              ) : null}
            </View>
          ) : sessionIsSubscription ? (
            <View style={[styles.costDisplay, styles.costDisplayMet]}>
              <Text style={styles.costLabel}>Subscription time remaining</Text>
              <View style={{ flexDirection: 'row', justifyContent: 'center', gap: spacing.xl }}>
                <View style={{ alignItems: 'center' }}>
                  <Text style={styles.costAmount}>
                    {/* null allowance = unknown (restored session) or unlimited — don't fake 0:00 */}
                    {sessionDailyMinutesAllowed == null ? '—' : formatTime(Math.max(0, Math.round(sessionDailyMinutesAllowed * 60 - elapsedSeconds)))}
                  </Text>
                  <Text style={styles.costRate}>today</Text>
                </View>
                <View style={{ alignItems: 'center' }}>
                  <Text style={styles.costAmount}>
                    {sessionMonthlyMinutesAllowed == null ? '—' : formatTime(Math.max(0, Math.round(sessionMonthlyMinutesAllowed * 60 - elapsedSeconds)))}
                  </Text>
                  <Text style={styles.costRate}>this month</Text>
                </View>
              </View>
              <Text style={styles.costRate}>Auto-stops at your daily or monthly limit</Text>
            </View>
          ) : (
            <View style={[styles.costDisplay, minimumMet ? styles.costDisplayMet : styles.costDisplayNotMet]}>
              {minimumMet ? (
                <>
                  <Text style={styles.costLabel}>Running Total</Text>
                  <Text style={styles.costAmount}>${currentCostDisplay}</Text>
                </>
              ) : (
                <>
                  <Ionicons name="alert-circle" size={22} color={colors.warning} />
                  <Text style={styles.costLabelWarning}>Minimum Charge of ${(minimumCharge / 100).toFixed(2)} Not Met</Text>
                </>
              )}
              <Text style={styles.costRate}>${bay.pricePerMinute?.toFixed(2)}/min</Text>
              {maximumCharge && (
                <Text style={[styles.costRate, !!maxChargeNear && { color: colors.danger, fontWeight: fontWeight.semibold as '600' }]}>
                  Max: ${(maximumCharge / 100).toFixed(2)}{maxChargeNear ? ' — approaching limit' : ''}
                </Text>
              )}
            </View>
          )}

          {sessionIsBundle ? (
            <Text style={styles.preAuthText}>
              Funded by your prepaid bundle · No card charge for this session
            </Text>
          ) : sessionIsSubscription ? (
            <Text style={styles.preAuthText}>
              Funded by your subscription · No card charge for this session
            </Text>
          ) : fleetSessionId ? (
            <Text style={styles.preAuthText}>
              Charging to {fleetAccounts.find(f => f.accountId === selectedFleetId)?.companyName || 'fleet'} · You'll only be charged for time used
            </Text>
          ) : preAuthorized ? (
            <Text style={styles.preAuthText}>
              ${(preAuthorized / 100).toFixed(2)} pre-authorized · You'll only be charged for time used
            </Text>
          ) : null}

          <View style={styles.washInfo}>
            <Text style={styles.washInfoText}>{location?.name}</Text>
            <Text style={styles.washInfoText}>{bay.name}</Text>
          </View>

          <TouchableOpacity style={styles.stopButton} onPress={fleetSessionId ? handleFleetStopSession : handleStopSession} disabled={purchasing} accessibilityRole="button" accessibilityState={{ disabled: purchasing }} accessibilityLabel={(sessionIsSubscription || sessionIsBundle) ? `End ${sessionNoun}` : `End ${sessionNoun} and pay`}>
            <View style={[styles.stopButtonInner, purchasing && { opacity: 0.85 }]}>
              {purchasing
                ? <ActivityIndicator size="small" color={colors.white} />
                : <Ionicons name="stop" size={24} color={colors.white} />}
            </View>
            <Text style={styles.stopButtonLabel}>
              {purchasing
                ? `Ending ${sessionNoun}…`
                : (sessionIsSubscription || sessionIsBundle)
                  ? (isMeteredVac ? 'End Vacuum' : 'End Wash')
                  : (isMeteredVac ? 'End Vacuum & Pay' : 'End Wash & Pay')}
            </Text>
          </TouchableOpacity>

          {timerMessage && (
            <View style={[styles.timerAlert, timerMessage.type === 'error' ? styles.timerAlertError : styles.timerAlertSuccess]}>
              <Ionicons
                name={timerMessage.type === 'error' ? 'warning' : 'checkmark-circle'}
                size={18}
                color={timerMessage.type === 'error' ? colors.warning : colors.success}
              />
              <Text style={styles.timerAlertText}>{timerMessage.text}</Text>
            </View>
          )}
        </ScrollView>

        {washComplete && (
          <WashCompleteOverlay
            data={washComplete}
            onDone={() => { setWashComplete(null); router.replace('/'); }}
            onReportProblem={() => { setReportTransactionId(washComplete.transactionId || washComplete.washId); setShowReport(true); }}
          />
        )}
        <ReportProblemModal
          visible={showReport}
          onClose={() => setShowReport(false)}
          transactionId={reportTransactionId}
          locationName={location?.name}
          bayName={bay?.name}
          amount={washComplete?.amount}
          type={bay?.type}
          tierName={washComplete?.tierName}
        />
      </>
    );
  }

  // Vacuum running / complete — simple client-side countdown (no live session
  // polling exists for vacuums; the controller stops the relay on its own).
  if (bay?.type === 'vacuum' && vacRunning) {
    const totalSec = Math.max(1, Math.ceil(vacRunning.minutes * 60));
    const vacProgress = (totalSec - vacRemainingSec) / totalSec;
    const vacMinsLeft = Math.floor(vacRemainingSec / 60);
    const vacSecsLeft = vacRemainingSec % 60;
    const vacMinutesLabel = Math.round(vacRunning.minutes);
    return (
      <>
        <Stack.Screen options={{ title: vacDone ? 'Vacuum Complete' : 'Vacuuming...' }} />
        <ScrollView style={{ flex: 1, backgroundColor: colors.background }} contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.lg }}>
          {vacDone ? (
            <>
              <View style={styles.successIconRing}>
                <Ionicons name="checkmark-circle" size={72} color={colors.success} />
              </View>
              <Text style={styles.successHeadline}>Vacuum Complete!</Text>
              <View style={styles.successDetailBadge}>
                <Ionicons name="sparkles" size={16} color={colors.primary} />
                <Text style={styles.successDetailText}>
                  {vacRunning.tierName} — {vacMinutesLabel} minutes
                  {vacRunning.kind === 'paid' ? ` · $${(vacRunning.amountCents / 100).toFixed(2)}` : ' · Free'}
                </Text>
              </View>
              <View style={styles.washInfo}>
                <Text style={[styles.washInfoText, { textAlign: 'center' }]}>{location?.name}</Text>
                <Text style={[styles.washInfoText, { textAlign: 'center' }]}>{bay.name}</Text>
              </View>
              <Button
                title="Done"
                size="lg"
                style={{ width: '100%', maxWidth: rs(360), marginTop: spacing.lg }}
                onPress={() => { setVacRunning(null); setVacDone(false); router.replace('/'); }}
              />
              {vacRunning.transactionId ? (
                <TouchableOpacity
                  onPress={() => { setReportTransactionId(vacRunning.transactionId!); setShowReport(true); }}
                  hitSlop={8}
                  style={{ marginTop: spacing.md }}
                >
                  <Text style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Report a problem</Text>
                </TouchableOpacity>
              ) : null}
            </>
          ) : (
            <>
              <View style={styles.timerPulse}>
                <Ionicons name="sparkles" size={28} color={colors.primary} />
              </View>
              <Text style={styles.timerLabel}>VACUUM RUNNING — {vacMinutesLabel} MINUTES</Text>
              <Text style={styles.timerDisplay}>
                {vacMinsLeft.toString().padStart(2, '0')}:{vacSecsLeft.toString().padStart(2, '0')}
              </Text>
              <Text style={styles.timerSubtext}>remaining</Text>
              <View style={styles.progressBarContainer}>
                <View style={[styles.progressBarFill, { width: `${Math.min(vacProgress * 100, 100)}%` }]} />
              </View>
              <View style={[styles.costDisplay, styles.costDisplayMet]}>
                <Text style={styles.costLabel}>
                  {vacRunning.kind === 'paid' ? 'Prepaid' : 'Free vacuum'}
                </Text>
                <Text style={styles.costAmount}>
                  {vacRunning.kind === 'paid' ? `$${(vacRunning.amountCents / 100).toFixed(2)}` : `${vacMinutesLabel} min`}
                </Text>
                <Text style={styles.costRate}>{vacRunning.tierName}</Text>
              </View>
              <View style={styles.washInfo}>
                <Text style={styles.washInfoText}>{location?.name}</Text>
                <Text style={styles.washInfoText}>{bay.name}</Text>
              </View>
              <Text style={{ fontSize: 12, color: colors.textLight, textAlign: 'center', marginTop: spacing.md, maxWidth: 260 }}>
                The vacuum will stop automatically when the timer reaches zero.
              </Text>
              {/* Stop is always available (paid AND grant runs). Warn first —
                  there is no refund of money or unused time either way. */}
              <Button
                title={vacStopping ? 'Stopping...' : 'Stop Vacuum'}
                variant="outline"
                size="sm"
                loading={vacStopping}
                style={{ marginTop: spacing.lg, minWidth: rs(200), borderColor: colors.danger }}
                textStyle={{ color: colors.danger }}
                icon={<Ionicons name="stop-circle-outline" size={16} color={colors.danger} />}
                onPress={() => confirmDestructive(
                  'Stop the vacuum?',
                  vacRunning.kind === 'grant'
                    ? 'Unused free minutes will be lost.'
                    : "You won't receive a refund for unused time.",
                  handleVacCountdownStop,
                  'Stop Vacuum',
                )}
              />
            </>
          )}
        </ScrollView>
        <ReportProblemModal
          visible={showReport}
          onClose={() => setShowReport(false)}
          transactionId={reportTransactionId}
          locationName={location?.name}
          bayName={bay?.name}
          amount={vacRunning.amountCents}
          type="vacuum"
          tierName={vacRunning.tierName}
        />
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{
        title: location?.name || 'Wash',
        headerBackTitle: 'Back',
        headerLeft: homeBackButton,
      }} />
      <ScrollView ref={scrollRef} style={styles.container} contentContainerStyle={styles.content}>
        {isMobileWeb && !dismissedBanner && (
          <View style={styles.downloadBanner}>
            <View style={{ flex: 1 }}>
              <Text style={styles.downloadBannerTitle}>Get the RinsePay App</Text>
              <Text style={styles.downloadBannerText}>For the best wash experience</Text>
            </View>
            <TouchableOpacity
              style={styles.downloadBannerBtn}
              onPress={() => router.push('/download')}
            >
              <Text style={styles.downloadBannerBtnText}>Get App</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setDismissedBanner(true)} hitSlop={8}>
              <Ionicons name="close" size={20} color={colors.textLight} />
            </TouchableOpacity>
          </View>
        )}

        {statusMessage && (
          <TouchableOpacity onPress={() => setStatusMessage(null)}>
            <View style={[
              styles.statusBanner,
              statusMessage.type === 'success' && styles.statusSuccess,
              statusMessage.type === 'error' && styles.statusError,
              statusMessage.type === 'info' && styles.statusInfo,
            ]}>
              <Ionicons
                name={statusMessage.type === 'success' ? 'checkmark-circle' : statusMessage.type === 'error' ? 'alert-circle' : 'information-circle'}
                size={20}
                color={statusMessage.type === 'success' ? colors.success : statusMessage.type === 'error' ? colors.danger : colors.primary}
              />
              <Text style={[
                styles.statusText,
                statusMessage.type === 'success' && { color: colors.success },
                statusMessage.type === 'error' && { color: colors.danger },
              ]}>{statusMessage.text}</Text>
              <Ionicons name="close" size={16} color={colors.textLight} />
            </View>
          </TouchableOpacity>
        )}

        {/* Controller offline warning banner — only during active session (initial load is handled by full-page block above) */}
        {sessionActive && controllerStatus?.hasController && !controllerStatus.controllerOnline && (
          <View style={styles.offlineBanner}>
            <View style={styles.offlineBannerContent}>
              <Ionicons name="wifi" size={22} color="#92400e" />
              <View style={{ flex: 1 }}>
                <Text style={styles.offlineBannerTitle}>Equipment may be offline</Text>
                <Text style={styles.offlineBannerText}>
                  {controllerStatus.message || 'The wash controller is not responding. You can still try to start a wash.'}
                </Text>
              </View>
            </View>
            <TouchableOpacity
              style={styles.offlineBannerBtn}
              disabled={controllerCheckLoading}
              onPress={async () => {
                setControllerCheckLoading(true);
                try {
                  const fresh = await api<ControllerStatus>(`/api/bays/${bayId}/controller-status`);
                  setControllerStatus(fresh);
                  if (fresh.controllerOnline) {
                    toast.show({ type: 'success', title: 'Controller is back online!' });
                  } else {
                    toast.show({ type: 'warning', title: 'Still offline', message: 'Controller is not responding.' });
                  }
                } catch {
                  toast.show({ type: 'error', title: 'Could not check status' });
                } finally {
                  setControllerCheckLoading(false);
                }
              }}
            >
              <Ionicons name="refresh" size={16} color="#92400e" />
              <Text style={styles.offlineBannerBtnText}>
                {controllerCheckLoading ? 'Checking...' : 'Refresh'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.locationBox}>
          <View style={styles.locationBoxHeader}>
            <Ionicons name="water" size={24} color={colors.white} />
            <Text style={styles.locationBoxName}>{location?.name}</Text>
          </View>
          <Text style={styles.locationBoxAddress}>
            {location?.address}, {location?.city}, {location?.state} {location?.zip}
          </Text>
          {location?.description && (
            <Text style={styles.locationBoxDesc}>{location.description}</Text>
          )}
          {location?.phone && (
            <View style={styles.locationBoxRow}>
              <Ionicons name="call" size={14} color="rgba(255,255,255,0.7)" />
              <Text style={styles.locationBoxDetail}>{location.phone}</Text>
            </View>
          )}
          {location?.hours && (
            <View style={styles.locationBoxHours}>
              {(() => {
                const entries = Object.entries(location.hours);
                const all247 = entries.length === 7 && entries.every(([, h]: [string, any]) => h.open === '00:00' && (h.close === '23:59' || h.close === '24:00'));
                if (all247) return <Text style={styles.locationBoxDetail}>Open 24/7</Text>;
                return entries.map(([day, h]: [string, any]) => (
                  <View key={day} style={styles.locationBoxHoursRow}>
                    <Text style={styles.locationBoxHoursDay}>{day.charAt(0).toUpperCase() + day.slice(1)}</Text>
                    <Text style={styles.locationBoxDetail}>
                      {h.closed ? 'Closed' : (h.open === '00:00' && (h.close === '23:59' || h.close === '24:00')) ? '24 Hours' : `${h.open} - ${h.close}`}
                    </Text>
                  </View>
                ));
              })()}
            </View>
          )}
        </View>

        <Card style={styles.bayCard}>
          <View style={styles.bayHeader}>
            <Ionicons name={bay?.type === 'automatic' ? 'car' : bay?.type === 'vacuum' ? 'sparkles' : 'timer'} size={20} color={colors.textSecondary} />
            <Text style={styles.bayName}>{bay?.name}</Text>
            <View style={styles.bayTypeBadge}>
              <Text style={styles.bayTypeText}>{bay?.type === 'automatic' ? 'Automatic' : bay?.type === 'vacuum' ? 'Vacuum' : 'Self-Serve'}</Text>
            </View>
          </View>
        </Card>

        {/* Gift credit — sits above the payment method choices. Stacks with a
            promo (promo pays first, then credit, then card). Hidden when a
            fleet account is selected (fleet pays, not the member) or a
            session is already running. */}
        {!selectedFleetId && !sessionActive && washCreditCents !== null && washCreditCents > 0 && (
          <Card style={[styles.selectorCard, useWashCredit && { borderColor: colors.primary, borderWidth: 2 }]}>
            <TouchableOpacity onPress={() => setUseWashCredit(!useWashCredit)} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Ionicons name={useWashCredit ? 'checkbox' : 'square-outline'} size={28} color={useWashCredit ? colors.primary : colors.textLight} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.selectorTitle, { marginBottom: 2 }]}>
                  Gift credit: ${(washCreditCents / 100).toFixed(2)}
                </Text>
                <Text style={{ color: colors.textSecondary, fontSize: 13 }}>
                  {useWashCredit
                    ? appliedVoucher
                      ? 'Covers whatever your promo doesn’t. Anything left after that goes to your card.'
                      : 'Pays for this wash first. Anything it doesn’t cover goes to your card.'
                    : 'Not being used — tap to pay with your credit.'}
                </Text>
              </View>
            </TouchableOpacity>
          </Card>
        )}

        {/* Voucher error from explicit ?voucher=CODE lookup */}
        {voucherError && (bay?.type === 'automatic' || bay?.type === 'self-serve' || bay?.type === 'vacuum') && (
          <Card style={styles.selectorCard}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Ionicons name="alert-circle" size={24} color={colors.danger} />
              <Text style={{ flex: 1, color: colors.danger, fontSize: 14 }}>{voucherError}</Text>
            </View>
          </Card>
        )}

        {/* Applied voucher banner — shows what's active + a Remove button */}
        {appliedVoucher && (bay?.type === 'automatic' || bay?.type === 'self-serve' || bay?.type === 'vacuum') && (
          <Card style={[styles.selectorCard, { borderColor: colors.primary, borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Ionicons name="gift" size={28} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.selectorTitle, { marginBottom: 2 }]}>
                  {appliedVoucher.discountKind === 'free_wash' || (appliedVoucher.discountKind === 'percent' && appliedVoucher.percentOff === 100)
                    ? 'Gift wash applied'
                    : appliedVoucher.discountKind === 'free_minutes'
                      ? `${appliedVoucher.freeMinutes ?? ''} free minutes applied`.replace('  ', ' ')
                      : `${appliedVoucher.percentOff}% off applied`}
                </Text>
                <Text style={{ color: colors.textSecondary, fontSize: 13 }}>
                  From {appliedVoucher.ownerName}{appliedVoucher.tierName ? ` — ${appliedVoucher.tierName}` : ''} · 1 use
                </Text>
                {/* Tier-mismatch warning is an automatic-bay concept; vacuum
                    validity rides the appliesTo.vacuum axis instead. */}
                {bay?.type === 'automatic' && !voucherAppliesToSelectedTier && appliedVoucher.tierName && (
                  <Text style={{ color: colors.warning, fontSize: 13, marginTop: 2, fontWeight: '600' as any }}>
                    Only valid for {appliedVoucher.tierName} — select that wash to use it.
                  </Text>
                )}
              </View>
              <TouchableOpacity onPress={handleClearVoucher} hitSlop={8}>
                <Text style={{ color: colors.primary, fontSize: 14, fontWeight: '600' as any }}>Remove</Text>
              </TouchableOpacity>
            </View>
          </Card>
        )}

        {/* Available vouchers — opt-in chooser. Hidden while one is applied.
            Vacuum bays included (mig 0064) — the ?bayId= fetch is server-filtered. */}
        {!appliedVoucher && availableVouchers.length > 0 && (bay?.type === 'automatic' || bay?.type === 'self-serve' || bay?.type === 'vacuum') && (
          <Card style={styles.selectorCard}>
            <Text style={styles.selectorTitle}>You have a gift available</Text>
            {availableVouchers.map(v => (
              <TouchableOpacity
                key={v.code}
                onPress={() => handleApplyVoucher(v)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.borderLight }}
              >
                <Ionicons name="gift-outline" size={22} color={colors.primary} />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 15, fontWeight: '600' as any, color: colors.text }}>
                    {v.discountKind === 'free_wash' || (v.discountKind === 'percent' && v.percentOff === 100)
                      ? (v.tierName ? `Free ${v.tierName} wash` : 'Free wash')
                      : v.discountKind === 'free_minutes'
                        ? `${(v as any).freeMinutes ?? '?'} free minutes`
                        : `${v.percentOff}% off${v.tierName ? ` ${v.tierName}` : ''}`}
                  </Text>
                  <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 2 }}>
                    From {v.ownerName} · 1 use
                  </Text>
                </View>
                <Text style={{ color: colors.primary, fontWeight: '600' as any }}>Apply</Text>
              </TouchableOpacity>
            ))}
          </Card>
        )}


        {bay?.type !== 'vacuum' && fleetAccounts.length > 0 && (
          <Card style={styles.bayCard}>
            <Text style={{ fontSize: 14, fontWeight: '600', color: colors.text, marginBottom: 8 }}>Payment Method</Text>
            <TouchableOpacity
              style={[styles.tierOption, !selectedFleetId && styles.tierSelected]}
              onPress={() => setSelectedFleetId(null)}
              accessibilityRole="radio"
              accessibilityState={{ selected: !selectedFleetId, checked: !selectedFleetId }}
              accessibilityLabel="Pay with card"
            >
              <View style={styles.tierLeft}>
                <View style={[styles.tierRadio, !selectedFleetId && styles.tierRadioSelected]}>
                  {!selectedFleetId && <View style={styles.tierRadioDot} />}
                </View>
                <Text style={[styles.tierName, !selectedFleetId && styles.tierNameSelected]}>Pay with Card</Text>
              </View>
              <Ionicons name="card" size={20} color={!selectedFleetId ? colors.primary : colors.textLight} />
            </TouchableOpacity>
            {fleetAccounts.map(fleet => (
              <TouchableOpacity
                key={fleet.accountId}
                style={[styles.tierOption, selectedFleetId === fleet.accountId && styles.tierSelected]}
                onPress={() => setSelectedFleetId(fleet.accountId)}
                accessibilityRole="radio"
                accessibilityState={{ selected: selectedFleetId === fleet.accountId, checked: selectedFleetId === fleet.accountId }}
                accessibilityLabel={`Charge to fleet account ${fleet.companyName}`}
              >
                <View style={styles.tierLeft}>
                  <View style={[styles.tierRadio, selectedFleetId === fleet.accountId && styles.tierRadioSelected]}>
                    {selectedFleetId === fleet.accountId && <View style={styles.tierRadioDot} />}
                  </View>
                  <Text style={[styles.tierName, selectedFleetId === fleet.accountId && styles.tierNameSelected]}>
                    {fleet.companyName}
                  </Text>
                </View>
                <Ionicons name="business" size={20} color={selectedFleetId === fleet.accountId ? colors.primary : colors.textLight} />
              </TouchableOpacity>
            ))}
          </Card>
        )}

        {/* Free-vac grant banner — prominent card; redeems via the automatic
            purchase endpoint with useVacGrant ($0, grantMinutes long). */}
        {bay?.type === 'vacuum' && vacGrants.length > 0 && (() => {
          const grant = vacGrants[0];
          return (
            <Card style={[styles.selectorCard, { borderColor: colors.success, borderWidth: 2 }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs }}>
                <Ionicons name="gift" size={22} color={colors.success} />
                <Text style={styles.selectorTitle}>Free Vacuum Available</Text>
              </View>
              <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
                {grant.minutesGranted} minutes free
                {grant.sourceTierName ? ` with your ${grant.sourceTierName} wash` : ''}
                {grant.expiresAt ? ` (expires ${new Date(grant.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})` : ''}
              </Text>
              <Button
                title="Start Free Vacuum"
                onPress={() => handleVacuumGrantRedeem(grant)}
                loading={purchasing}
                size="lg"
                style={styles.startBtn}
                icon={<Ionicons name="play" size={20} color={colors.white} />}
              />
            </Card>
          );
        })()}

        {/* Vacuum membership card — start from the plan's vacuum-minute bucket.
            Only tiers whose minutes fit the remaining bucket are offered
            (remaining null = unlimited). */}
        {bay?.type === 'vacuum' && subCheck?.hasSubscription && subCheck.bayType === 'vacuum' && !subCheck.suspended && (
          <Card style={[styles.selectorCard, { borderColor: colors.primary, borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
              <Ionicons name="ribbon" size={22} color={colors.primary} />
              <Text style={styles.selectorTitle}>Membership Vacuum</Text>
            </View>
            <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
              {subCheck.planName} — Vacuum minutes included
            </Text>
            {subCheck.canWash ? (
              <>
                <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginBottom: spacing.md }}>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ fontSize: 24, fontWeight: fontWeight.bold, color: colors.primary }}>
                      {subCheck.dailyVacMinutesRemaining == null ? '∞' : formatMinutes1(subCheck.dailyVacMinutesRemaining)}
                    </Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>min left today</Text>
                  </View>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ fontSize: 24, fontWeight: fontWeight.bold, color: colors.primary }}>
                      {subCheck.monthlyVacMinutesRemaining == null ? '∞' : formatMinutes1(subCheck.monthlyVacMinutesRemaining)}
                    </Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>min left this month</Text>
                  </View>
                </View>
                {/* Single entry point: a live count-up membership session
                    (draws the VAC buckets server-side, auto-stops at the
                    limit). Works on every vacuum — package-mode too, since
                    all vacuums carry the master rate. */}
                <Button
                  title="Start with Membership"
                  onPress={() => startMembershipRedemption('selfserve')}
                  loading={purchasing}
                  size="lg"
                  style={{ marginBottom: spacing.sm }}
                  icon={<Ionicons name="ribbon" size={20} color={colors.white} />}
                />
                <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs, textAlign: 'center' }}>
                  Pay per-use available below if you prefer.
                </Text>
              </>
            ) : (
              <View style={{ padding: spacing.md, backgroundColor: colors.warning + '10', borderRadius: borderRadius.md }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                  <Ionicons name="information-circle" size={18} color={colors.warning} />
                  <Text style={{ fontSize: fontSize.sm, color: colors.warning, fontWeight: fontWeight.medium }}>
                    {(subCheck.dailyVacMinutesRemaining ?? 0) <= 0 ? 'Daily vacuum minutes used up' : 'Monthly vacuum minutes used up'}
                  </Text>
                </View>
                <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
                  Pay per use below.
                </Text>
              </View>
            )}
          </Card>
        )}

        {/* Vacuum bundle card — spend prepaid vac minutes on tiers that fit. */}
        {bay?.type === 'vacuum' && vacBundles.length > 0 && (
          <Card style={[styles.selectorCard, { borderColor: colors.primary, borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
              <Ionicons name="cube" size={22} color={colors.primary} />
              <Text style={styles.selectorTitle}>Prepaid Bundle Vacuum</Text>
            </View>
            {/* One button per bundle — no minutes/tier pre-select. The session
                claims min(remaining, bay max) up front (the full remainder for
                all-at-once items) and the count-up screen takes over; stopping
                early refunds unused minutes minus the bay minimum. */}
            {vacBundles.map(b => (
              <View key={b.purchasedBundleId} style={{ padding: spacing.md, backgroundColor: colors.background, borderRadius: borderRadius.md, marginBottom: spacing.sm }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.text }}>{b.planName}</Text>
                  <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>{b.remainingMinutes} min left</Text>
                </View>
                {b.expiresAt && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.warning, marginBottom: spacing.sm }}>
                    Expires {new Date(b.expiresAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </Text>
                )}
                {b.allAtOnce && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.sm }}>
                    Must be used in one session — stopping early forfeits the rest.
                  </Text>
                )}
                <Button
                  title={`Start with Bundle — ${b.remainingMinutes} min left`}
                  onPress={() => handleVacuumBundleStart(b)}
                  loading={purchasing}
                  disabled={b.remainingMinutes < 1}
                  size="lg"
                  icon={<Ionicons name="cube" size={20} color={colors.white} />}
                />
              </View>
            ))}
          </Card>
        )}

        {/* Vacuum tier selector — timed tiers with name, minutes, and price.
            No fleet option at vacuums; a promo voucher discounts first
            (percent or free-minutes cash value, mirroring the backend), then
            wash credit stacks via the gift-credit toggle above. */}
        {bay?.type === 'vacuum' && bay.tiers && (() => {
          const visibleTiers = bay.tiers.filter(t => t.enabled !== false);
          if (visibleTiers.length === 0) return null;
          const selectedVacTier = visibleTiers.find(t => t.name === selectedTier);
          const selectedTierPrice = selectedVacTier?.price ?? 0;
          const promoCents = selectedVacTier ? vacuumVoucherDiscount(selectedVacTier) : 0;
          const afterPromo = Math.max(0, selectedTierPrice - promoCents);
          const { draw: creditDraw, card: cardCents } = creditSplit(afterPromo);
          const buttonLabel = !selectedTier
            ? 'Select a Vacuum Time'
            : cardCents === 0 && (promoCents > 0 || creditDraw > 0)
              ? `Start Vacuum — Free (${promoCents > 0 && creditDraw > 0 ? 'promo + credit' : promoCents > 0 ? 'promo applied' : 'credit applied'})`
              : promoCents > 0 || creditDraw > 0
                ? `Pay $${(cardCents / 100).toFixed(2)}${promoCents > 0 ? ` (promo −$${(promoCents / 100).toFixed(2)})` : ''}${creditDraw > 0 ? ` (credit −$${(creditDraw / 100).toFixed(2)})` : ''}`
                : `Pay $${(selectedTierPrice / 100).toFixed(2)}`;
          return (
            <Card style={styles.selectorCard}>
              <Text style={styles.selectorTitle}>Select Your Vacuum Time</Text>
              {visibleTiers.map(tier => {
                const selected = selectedTier === tier.name;
                const mins = Math.round((tier.durationMs ?? 0) / 60000);
                return (
                  <TouchableOpacity
                    key={tier.name}
                    style={[styles.tierOption, selected && styles.tierSelected]}
                    onPress={() => setSelectedTier(tier.name)}
                    accessibilityRole="radio"
                    accessibilityState={{ selected, checked: selected }}
                    accessibilityLabel={`${tier.name}, ${mins} minutes, $${(tier.price / 100).toFixed(2)}`}
                  >
                    <View style={styles.tierLeft}>
                      <View style={[styles.tierRadio, selected && styles.tierRadioSelected]}>
                        {selected && <View style={styles.tierRadioDot} />}
                      </View>
                      <View>
                        <Text style={[styles.tierName, selected && styles.tierNameSelected]}>{tier.name}</Text>
                        <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>{mins} min</Text>
                      </View>
                    </View>
                    <Text style={[styles.tierPrice, selected && styles.tierPriceSelected]}>
                      ${(tier.price / 100).toFixed(2)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
              <View style={styles.purchaseSection}>
                <Button
                  title={buttonLabel}
                  onPress={handleVacuumPurchase}
                  loading={purchasing}
                  disabled={!selectedTier}
                  size="lg"
                  style={styles.purchaseBtn}
                />
              </View>
            </Card>
          );
        })()}

        {bay && isSessionBay
          && (selfServePackages.length > 0 || (bay.presets && bay.presets.length > 0) || bay.allowCustomAmount) && (
          <Card style={styles.selectorCard}>
            <Text style={styles.selectorTitle}>{isMeteredVac ? 'Select Vacuum Amount' : 'Select Wash Amount'}</Text>
            <Text style={styles.selfServeDesc}>
              {!selectedFleetId && appliedVoucher
                ? 'Choose how much time you’d like. Your promo pays first, then gift credit, then your card.'
                : !selectedFleetId && useWashCredit && (washCreditCents ?? 0) > 0
                  ? 'Choose how much time you’d like. Your gift credit pays first; your card covers the rest.'
                  : 'Choose how much time you\'d like. Your card will be charged the selected amount.'}
            </Text>
            {/* Timed packages — named prepaid options with their own minutes
                (may be non-linear vs the rate). Hidden for fleet payment,
                which bills straight amounts at the rate. */}
            {!selectedFleetId && selfServePackages.map(pkg => {
              const selected = selectedPackage === pkg.name;
              const mins = Math.round((pkg.durationMs ?? 0) / 60000);
              return (
                <TouchableOpacity
                  key={pkg.name}
                  style={[styles.tierOption, selected && styles.tierSelected]}
                  onPress={() => { setSelectedPackage(pkg.name); setSelectedPreset(null); setCustomAmountText(''); setCustomAmountError(null); }}
                  accessibilityRole="radio"
                  accessibilityState={{ selected, checked: selected }}
                  accessibilityLabel={`${pkg.name}, ${mins} minutes, $${(pkg.price / 100).toFixed(2)}`}
                >
                  <View style={styles.tierLeft}>
                    <View style={[styles.tierRadio, selected && styles.tierRadioSelected]}>
                      {selected && <View style={styles.tierRadioDot} />}
                    </View>
                    <View>
                      <Text style={[styles.tierName, selected && styles.tierNameSelected]}>{pkg.name}</Text>
                      <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>{mins} min</Text>
                    </View>
                  </View>
                  <Text style={[styles.tierPrice, selected && styles.tierPriceSelected]}>
                    ${(pkg.price / 100).toFixed(2)}
                  </Text>
                </TouchableOpacity>
              );
            })}
            {bay.presets && bay.presets.length > 0 && (
              <View style={styles.presetGrid}>
                {bay.presets.map((amount) => {
                  const minutesRaw = bay.pricePerMinute ? amount / (bay.pricePerMinute * 100) : 0;
                  return (
                    <TouchableOpacity
                      key={amount}
                      style={[styles.presetBtn, selectedPreset === amount && styles.presetBtnSelected]}
                      onPress={() => { setSelectedPreset(amount); setSelectedPackage(null); setCustomAmountText(''); }}
                      accessibilityRole="radio"
                      accessibilityState={{ selected: selectedPreset === amount, checked: selectedPreset === amount }}
                      accessibilityLabel={`$${(amount / 100).toFixed(2)} preset`}
                    >
                      <Text style={[styles.presetPrice, selectedPreset === amount && styles.presetPriceSelected]}>
                        ${(amount / 100).toFixed(2)}
                      </Text>
                      {minutesRaw > 0 && (
                        <Text style={[styles.presetTime, selectedPreset === amount && styles.presetTimeSelected]}>
                          ~{minutesRaw.toFixed(2)} min
                        </Text>
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>
            )}
            {bay.allowCustomAmount && (
              <>
                <View style={styles.customAmountRow}>
                  <Text style={styles.customAmountLabel}>Custom amount:</Text>
                  <View style={styles.customAmountInput}>
                    <Text style={styles.customAmountPrefix}>$</Text>
                    <TextInput
                      style={styles.customAmountField}
                      placeholder="0.00"
                      placeholderTextColor={colors.textLight}
                      keyboardType="decimal-pad"
                      value={customAmountText}
                      onChangeText={(t) => { setCustomAmountText(t); setSelectedPreset(null); setSelectedPackage(null); setCustomAmountError(null); }}
                      maxLength={7}
                    />
                  </View>
                </View>
                {/* Owner-configured custom-amount bounds (wave 2) — shown up
                    front so nobody types an amount just to get rejected. */}
                {(bay.customAmountMinCents != null || bay.customAmountMaxCents != null) && (
                  <Text style={styles.customAmountBounds}>
                    {bay.customAmountMinCents != null && bay.customAmountMaxCents != null
                      ? `Between $${(bay.customAmountMinCents / 100).toFixed(2)} and $${(bay.customAmountMaxCents / 100).toFixed(2)}`
                      : bay.customAmountMinCents != null
                        ? `At least $${(bay.customAmountMinCents / 100).toFixed(2)}`
                        : `Up to $${(bay.customAmountMaxCents! / 100).toFixed(2)}`}
                  </Text>
                )}
              </>
            )}
            {customAmountError && <Text style={styles.customAmountError}>{customAmountError}</Text>}
            <Button
              title={(() => {
                const verb = isMeteredVac ? 'Start Vacuum' : 'Start Wash';
                const pkg = !selectedFleetId && selectedPackage
                  ? selfServePackages.find(p => p.name === selectedPackage) : undefined;
                const amt = pkg?.price
                  || selectedPreset
                  || (customAmountText ? Math.round(parseFloat(customAmountText) * 100) : 0);
                if (amt <= 0) return 'Select an Amount';
                if (selectedFleetId) return `Charge to Fleet — $${(amt / 100).toFixed(2)}`;
                const promo = selfServeVoucherDiscount(amt);
                const { draw, card } = creditSplit(amt - promo);
                const label = pkg ? `${verb} — ${pkg.name}` : verb;
                if (promo + draw === 0) return `${label} — $${(amt / 100).toFixed(2)}`;
                if (card === 0) return `${label} — Free (rewards applied)`;
                return `${label} — $${(card / 100).toFixed(2)} (rewards −$${((promo + draw) / 100).toFixed(2)})`;
              })()}
              onPress={() => {
                // Named package first — its minutes come from the package, not
                // the rate, so no min/max validation applies.
                if (!selectedFleetId && selectedPackage) {
                  const pkg = selfServePackages.find(p => p.name === selectedPackage);
                  if (pkg) handlePresetPurchase(pkg.price, pkg.name);
                  return;
                }
                const amt = selectedPreset || (customAmountText ? Math.round(parseFloat(customAmountText) * 100) : 0);
                if (amt > 0) {
                  const isCustom = !selectedPreset && !!customAmountText;
                  // Owner custom-amount bounds (wave 2) take precedence; the
                  // legacy metered min/max stays as the fallback bound.
                  const effMin = bay.customAmountMinCents ?? bay.minimumCharge;
                  const effMax = bay.customAmountMaxCents ?? bay.maximumCharge;
                  if (isCustom && effMin != null && effMin > 0 && amt < effMin) {
                    setCustomAmountError(`Amount too low — the minimum custom amount here is $${(effMin / 100).toFixed(2)}`);
                    return;
                  }
                  if (isCustom && effMax != null && amt > effMax) {
                    setCustomAmountError(`Amount too high — the maximum custom amount here is $${(effMax / 100).toFixed(2)}`);
                    return;
                  }
                  setCustomAmountError(null);
                  if (selectedFleetId) {
                    handleFleetPurchase('self-serve', amt);
                  } else {
                    handlePresetPurchase(amt);
                  }
                }
              }}
              loading={purchasing}
              disabled={(selectedFleetId ? (!selectedPreset && !customAmountText) : (!selectedPackage && !selectedPreset && !customAmountText))}
              size="lg"
              style={styles.startBtn}
              icon={<Ionicons name="play" size={20} color={colors.white} />}
            />
          </Card>
        )}

        {/* Count-up (metered) session — shown whenever the metered family is
            configured (max charge set), independent of packages/presets. */}
        {bay && countUpAvailable && (
          <Card style={styles.selectorCard}>
            <View style={styles.selfServeInfo}>
              <Ionicons name={isMeteredVac ? 'sparkles' : 'timer-outline'} size={40} color={colors.primary} />
              <Text style={styles.selfServeTitle}>{isMeteredVac ? 'Count-Up Vacuum' : 'Count-Up Wash'}</Text>
              <Text style={styles.selfServeDesc}>
                {selectedFleetId
                  ? 'Start washing and pay for the time you use. Charged to your fleet account.'
                  : appliedVoucher?.discountKind === 'free_minutes'
                    ? `Your first ${appliedVoucher.freeMinutes ?? ''} minutes are free. Keep washing after that and the extra time bills normally${useWashCredit && (washCreditCents ?? 0) > 0 ? ' — gift credit first, then your card' : ' to your card'}.`.replace('  ', ' ')
                    : appliedVoucher
                      ? 'Your gift covers this wash — no charge to your card.'
                      : useWashCredit && (washCreditCents ?? 0) > 0
                        ? `Start ${isMeteredVac ? 'vacuuming' : 'washing'} and pay for the time you use. Your gift credit pays first when you stop; your card covers the rest (pre-authorized for the maximum charge).`
                        : `Start ${isMeteredVac ? 'vacuuming' : 'washing'} and pay for the time you use. Your card will be pre-authorized for the maximum charge.`}
              </Text>
              <View style={styles.rateBox}>
                <Text style={styles.rateAmount}>${bay.pricePerMinute?.toFixed(2)}</Text>
                <Text style={styles.rateUnit}>per minute</Text>
              </View>
              {bay.minimumCharge != null && (
                <Text style={styles.minChargeText}>Minimum charge: ${(bay.minimumCharge / 100).toFixed(2)}</Text>
              )}
              {bay.maximumCharge != null && (
                <Text style={styles.minChargeText}>Maximum charge: ${(bay.maximumCharge / 100).toFixed(2)}{selectedFleetId ? '' : ' (pre-authorized)'}</Text>
              )}
            </View>
            <Button
              title={selectedFleetId
                ? 'Start Wash (Fleet)'
                : appliedVoucher?.discountKind === 'free_minutes'
                  ? `Start Wash — first ${appliedVoucher.freeMinutes ?? ''} min free`.replace('  ', ' ')
                  : appliedVoucher
                    ? 'Start Wash (gift applied)'
                    : useWashCredit && (washCreditCents ?? 0) > 0
                      ? `Start ${isMeteredVac ? 'Vacuum' : 'Wash'} — using $${(washCreditCents! / 100).toFixed(2)} credit`
                      : isMeteredVac ? 'Start Vacuum' : 'Start Wash'}
              onPress={() => {
                if (selectedFleetId) {
                  handleFleetStartSession();
                } else {
                  handleStartSession();
                }
              }}
              loading={purchasing}
              size="lg"
              style={styles.startBtn}
              icon={<Ionicons name="play" size={20} color={colors.white} />}
            />
          </Card>
        )}

        {/* Suspended membership notice — replaces the membership wash cards.
            Per-wash payment below stays fully available. */}
        {!selectedFleetId && subCheck?.hasSubscription && subCheck.suspended && (
          <Card style={[styles.selectorCard, { borderColor: colors.danger + '60', borderWidth: 1 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs }}>
              <Ionicons name="pause-circle" size={20} color={colors.danger} />
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.text }}>Membership Suspended</Text>
            </View>
            <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary, lineHeight: 20 }}>
              Your {subCheck.planName} membership is currently suspended and can't be used for washes. Billing is paused while suspended. Please contact the car wash. You can still pay per wash below.
            </Text>
          </Card>
        )}

        {/* Subscription membership wash card — shown ABOVE regular tier selector */}
        {bay?.type === 'automatic' && !selectedFleetId && subCheck?.hasSubscription && !subCheck.suspended && (
          <Card style={[styles.selectorCard, { borderColor: colors.primary, borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
              <Ionicons name="ribbon" size={22} color={colors.primary} />
              <Text style={styles.selectorTitle}>Membership Wash</Text>
            </View>
            <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
              {subCheck.planName} — {getTierLabel(subCheck.tierName ?? '', bay?.tiers)}
            </Text>
            {subCheck.canWash ? (
              <>
                <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginBottom: spacing.md }}>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ fontSize: 24, fontWeight: fontWeight.bold, color: colors.primary }}>{subCheck.dailyRemaining == null ? '∞' : subCheck.dailyRemaining}</Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>left today</Text>
                  </View>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ fontSize: 24, fontWeight: fontWeight.bold, color: colors.primary }}>{subCheck.monthlyRemaining == null ? '∞' : subCheck.monthlyRemaining}</Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>left this month</Text>
                  </View>
                </View>
                <Button
                  title={`Start ${getTierLabel(subCheck.tierName ?? '', bay?.tiers)} Wash (Included)`}
                  onPress={() => startMembershipRedemption('automatic')}
                  loading={purchasing}
                  size="lg"
                  icon={<Ionicons name="ribbon" size={20} color={colors.white} />}
                />
              </>
            ) : (
              <View style={{ padding: spacing.md, backgroundColor: colors.warning + '10', borderRadius: borderRadius.md }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                  <Ionicons name="information-circle" size={18} color={colors.warning} />
                  <Text style={{ fontSize: fontSize.sm, color: colors.warning, fontWeight: fontWeight.medium }}>
                    {subCheck.dailyRemaining != null && subCheck.dailyRemaining <= 0 ? 'Daily limit reached' : 'Monthly limit reached'}
                  </Text>
                </View>
                <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
                  Pay per-wash below.
                </Text>
              </View>
            )}
          </Card>
        )}

        {/* Subscription self-serve card — shown below the tier/preset selector for self-serve bays */}
        {bay?.type === 'self-serve' && !selectedFleetId && subCheck?.hasSubscription && !subCheck.suspended && !sessionActive && (
          <Card style={[styles.selectorCard, { borderColor: colors.primary, borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
              <Ionicons name="ribbon" size={22} color={colors.primary} />
              <Text style={styles.selectorTitle}>Membership Wash</Text>
            </View>
            <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
              {subCheck.planName} — Self-Serve
            </Text>
            {subCheck.canWash ? (
              <>
                <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginBottom: spacing.md }}>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ fontSize: 24, fontWeight: fontWeight.bold, color: colors.primary }}>
                      {subCheck.dailyMinutesRemaining == null ? '∞' : subCheck.dailyMinutesRemaining.toFixed(0)}
                    </Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>min left today</Text>
                  </View>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ fontSize: 24, fontWeight: fontWeight.bold, color: colors.primary }}>
                      {subCheck.monthlyMinutesRemaining == null ? '∞' : subCheck.monthlyMinutesRemaining.toFixed(0)}
                    </Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>min left this month</Text>
                  </View>
                </View>
                <Button
                  title="Start with Subscription"
                  onPress={() => startMembershipRedemption('selfserve')}
                  loading={purchasing}
                  size="lg"
                  icon={<Ionicons name="ribbon" size={20} color={colors.white} />}
                />
                <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.sm, textAlign: 'center' }}>
                  Pay per-use available above if you prefer.
                </Text>
              </>
            ) : (
              <View style={{ padding: spacing.md, backgroundColor: colors.warning + '10', borderRadius: borderRadius.md }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                  <Ionicons name="information-circle" size={18} color={colors.warning} />
                  <Text style={{ fontSize: fontSize.sm, color: colors.warning, fontWeight: fontWeight.medium }}>
                    {(subCheck.dailyMinutesRemaining ?? 0) <= 0 ? 'Daily minute limit reached' : 'Monthly minute limit reached'}
                  </Text>
                </View>
                <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.xs }}>
                  Pay per-use above.
                </Text>
              </View>
            )}
          </Card>
        )}

        {/* Bundle wash card — shown when user has eligible bundles */}
        {bay?.type === 'automatic' && !selectedFleetId && bundleCheck?.hasBundles && bundleCheck.bundles.length > 0 && (
          <View onLayout={e => { bundleUseY.current = e.nativeEvent.layout.y; }}>
          <Card style={[styles.selectorCard, { borderColor: colors.primary, borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
              <Ionicons name="cube" size={22} color={colors.primary} />
              <Text style={styles.selectorTitle}>Prepaid Bundle Wash</Text>
            </View>
            {bundleCheck.bundles.map(b => (
              <View key={b.purchasedBundleId} style={{ padding: spacing.md, backgroundColor: colors.background, borderRadius: borderRadius.md, marginBottom: spacing.sm }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.text }}>{b.planName}</Text>
                  <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>{getTierLabel(b.tierName, bay?.tiers)}</Text>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginBottom: spacing.md }}>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ fontSize: 24, fontWeight: fontWeight.bold, color: colors.primary }}>{b.washesRemaining}</Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>remaining</Text>
                  </View>
                  <View style={{ alignItems: 'center' }}>
                    <Text style={{ fontSize: 24, fontWeight: fontWeight.bold, color: colors.textSecondary }}>{b.washesTotal}</Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>total</Text>
                  </View>
                </View>
                {b.expiresAt && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.warning, marginBottom: spacing.sm, textAlign: 'center' }}>
                    Expires {new Date(b.expiresAt).toLocaleDateString()}
                  </Text>
                )}
                <Button
                  title={`Start ${getTierLabel(b.tierName, bay?.tiers)} Wash (Prepaid)`}
                  onPress={() => handleBundleWash(b.purchasedBundleId)}
                  loading={purchasing}
                  size="lg"
                  icon={<Ionicons name="cube" size={20} color={colors.white} />}
                />
              </View>
            ))}
          </Card>
          </View>
        )}

        {/* Membership that doesn't cover this bay — informational only. The
            backend returns no purchasable plans in this state (already a live
            member), so this card explains why instead of an empty plans list.
            Single-wash and bundle purchasing stay fully available. */}
        {!selectedFleetId && !subCheck?.hasSubscription && otherSub && (
          <Card style={[styles.selectorCard, { borderColor: colors.primary + '40', borderWidth: 1 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs }}>
              <Ionicons name="ribbon-outline" size={20} color={colors.primary} />
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.text }}>Your Membership</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm }}>
              <Ionicons name="information-circle" size={18} color={colors.primary} style={{ marginTop: 1 }} />
              <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.textSecondary, lineHeight: 20 }}>
                {describeOtherSubscription(otherSub)}
              </Text>
            </View>
          </Card>
        )}

        {/* Available membership plans — shown when user has no subscription
            (both bay types). Collapsed behind an expandable header so the
            scroll to single-pay options stays short; placement unchanged. */}
        {!selectedFleetId && !subCheck?.hasSubscription && availablePlans.length > 0 && (
          <CollapsibleSection
            title="Membership Plans"
            subtitle="Unlimited-style monthly plans — tap to view"
            icon="ribbon-outline"
            iconColor={colors.primary}
            style={[styles.selectorCard, { borderColor: colors.primary + '40', borderWidth: 1 }]}
          >
            <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.md }}>
              Subscribe at a fixed monthly price. Restrictions apply per plan.
            </Text>
            {availablePlans.map(plan => {
              const planType = (plan as any).planType ?? 'automatic';
              const showAuto = planType === 'automatic' || planType === 'mixed';
              const showSelf = planType === 'self_serve' || planType === 'mixed';
              return (
                <View key={plan.id} style={{ padding: spacing.md, backgroundColor: colors.background, borderRadius: borderRadius.md, marginBottom: spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                    <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.text }}>{plan.name}</Text>
                    <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.bold, color: colors.primary }}>
                      ${(plan.priceMonthly / 100).toFixed(2)}<Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.normal, color: colors.textSecondary }}>/mo</Text>
                    </Text>
                  </View>
                  {plan.description && (
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.xs }}>{plan.description}</Text>
                  )}
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm }}>
                    {showAuto && plan.tierName && (
                      <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>{getTierLabel(plan.tierName, bay?.tiers)}</Text>
                    )}
                    {showAuto && (
                      <>
                        <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>{plan.maxWashesPerDay}/day washes</Text>
                        <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>{plan.maxWashesPerMonth}/mo washes</Text>
                      </>
                    )}
                    {showSelf && (
                      <>
                        <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>{(plan as any).maxMinutesPerDay} min/day</Text>
                        <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>{(plan as any).maxMinutesPerMonth} min/mo</Text>
                      </>
                    )}
                    <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>
                      {plan.locationScope === 'all' ? 'All locations' : plan.locationName || 'This location'}
                    </Text>
                  </View>
                  {/* Vacuum-minute + vending-credit benefits — always visible
                      wherever a plan can be bought (Nathan 2026-08-16). */}
                  {vacMinutesLabel(plan.maxVacMinutesPerDay, plan.maxVacMinutesPerMonth) && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: spacing.xs }}>
                      <Ionicons name="sparkles" size={13} color={colors.primary} />
                      <Text style={{ fontSize: fontSize.xs, color: colors.primary, fontWeight: fontWeight.medium }}>
                        {vacMinutesLabel(plan.maxVacMinutesPerDay, plan.maxVacMinutesPerMonth)}
                      </Text>
                    </View>
                  )}
                  {vendingCreditLabel(plan.vendingCreditCentsPerMonth) && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: spacing.xs }}>
                      <Ionicons name="fast-food-outline" size={13} color={colors.primary} />
                      <Text style={{ fontSize: fontSize.xs, color: colors.primary, fontWeight: fontWeight.medium }}>
                        {vendingCreditLabel(plan.vendingCreditCentsPerMonth)}
                      </Text>
                    </View>
                  )}
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: spacing.sm }}>
                    <Ionicons name="car-outline" size={13} color={colors.textSecondary} />
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                      Valid for a single registered vehicle
                    </Text>
                  </View>
                  <Button
                    title={subscribing === plan.id ? 'Subscribing...' : `Subscribe — $${(plan.priceMonthly / 100).toFixed(2)}/mo`}
                    onPress={() => handleSubscribe(plan.id)}
                    loading={subscribing === plan.id}
                    disabled={!!subscribing}
                    size="sm"
                  />
                </View>
              );
            })}
          </CollapsibleSection>
        )}

        {/* Available bundles upsell — shown when user has no active bundles.
            Collapsed behind an expandable header (placement unchanged). Also
            shown at vacuum bays (backend filters to vac-minute bundles). */}
        {(bay?.type === 'automatic' || bay?.type === 'vacuum') && !selectedFleetId && availableBundles.length > 0 && (
          <CollapsibleSection
            title={(bundleCheck?.hasBundles || vacBundles.length > 0) ? 'Buy More Bundles' : 'Bundles'}
            subtitle="Prepaid packs at a discount — tap to view"
            icon="cube-outline"
            iconColor={colors.success}
            style={[styles.selectorCard, { borderColor: colors.success + '40', borderWidth: 1 }]}
          >
            <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.md }}>
              Save with prepaid bundles. Buy once, use anytime.
            </Text>
            {availableBundles.map(ab => {
              const itemsSummary = bundleItemsSummary(ab.items);
              const hasMultiItems = !!ab.items && ab.items.length > 0;
              const retailValue = hasMultiItems ? 0 : getTierPrice(ab.tierName, bay.tiers) * ab.washCount;
              const savings = retailValue > 0 ? Math.round(((retailValue - ab.bundlePrice) / retailValue) * 100) : 0;
              // Active bundles of this SAME plan — surfaced so a repurchase is a
              // conscious choice, not an accident. (BundleCheckItem carries no
              // plan id, so we match on the plan name.)
              const ownedRemaining = bay.type === 'vacuum'
                ? vacBundles.filter(b => b.planName === ab.name).reduce((sum, b) => sum + b.remainingMinutes, 0)
                : (bundleCheck?.bundles ?? [])
                  .filter(b => b.planName === ab.name)
                  .reduce((sum, b) => sum + b.washesRemaining, 0);
              return (
                <View key={ab.id} style={{ padding: spacing.md, backgroundColor: colors.background, borderRadius: borderRadius.md, marginBottom: spacing.sm }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                    <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.text }}>{ab.name}</Text>
                    <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.bold, color: colors.primary }}>
                      ${(ab.bundlePrice / 100).toFixed(2)}
                    </Text>
                  </View>
                  {ownedRemaining > 0 && (
                    <View style={styles.ownedBadge}>
                      <Ionicons name="checkmark-circle" size={14} color={colors.success} />
                      <Text style={styles.ownedBadgeText}>
                        You own this — {ownedRemaining} {bay.type === 'vacuum' ? 'min' : `wash${ownedRemaining === 1 ? '' : 'es'}`} left
                      </Text>
                    </View>
                  )}
                  {ab.description && (
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.xs }}>{ab.description}</Text>
                  )}
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm }}>
                    {itemsSummary ? (
                      <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>{itemsSummary}</Text>
                    ) : (
                      <>
                        <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>{getTierLabel(ab.tierName, bay.tiers)}</Text>
                        <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>{ab.washCount} washes</Text>
                      </>
                    )}
                    {savings > 0 && (
                      <Text style={{ fontSize: fontSize.xs, color: colors.success, fontWeight: fontWeight.semibold }}>Save {savings}%</Text>
                    )}
                    {ab.expirationMinutes ? (
                      <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>Same-visit ({ab.expirationMinutes} min)</Text>
                    ) : ab.expirationDays ? (
                      <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>{ab.expirationDays} day expiry</Text>
                    ) : null}
                  </View>
                  <Button
                    title={purchasingBundle === ab.id
                      ? 'Purchasing...'
                      : hasMultiItems
                        ? `Buy Bundle — $${(ab.bundlePrice / 100).toFixed(2)}`
                        : `Buy ${ab.washCount} Washes — $${(ab.bundlePrice / 100).toFixed(2)}`}
                    onPress={() => handleBundlePurchase(ab.id)}
                    loading={purchasingBundle === ab.id}
                    disabled={!!purchasingBundle}
                    size="sm"
                  />
                </View>
              );
            })}
          </CollapsibleSection>
        )}

        {bay?.type === 'automatic' && bay.tiers && (() => {
          // Owner can disable individual tiers; never offer them to customers.
          const visibleTiers = bay.tiers.filter(t => t.enabled !== false);
          if (visibleTiers.length === 0) return null;
          // The "Most Popular" badge shows on tiers[0] (index 0) by convention,
          // but only if that tier is NOT the cheapest option — we'd rather
          // show no badge than nudge customers toward the lowest-revenue tier.
          const minPrice = Math.min(...visibleTiers.map(t => t.price));
          const popularIdx = visibleTiers[0].price > minPrice ? 0 : -1;
          // If a voucher is applied AND covers the selected tier, compute the
          // effective price for the button label. A tier-scoped voucher on a
          // different tier doesn't discount (the backend would reject it), so the
          // button shows the full sticker price for that tier.
          const selectedTierPrice = visibleTiers.find(t => t.name === selectedTier)?.price || 0;
          const isFullGift = voucherAppliesToSelectedTier && (
            appliedVoucher!.discountKind === 'free_wash' ||
            (appliedVoucher!.discountKind === 'percent' && appliedVoucher!.percentOff === 100)
          );
          const discountCents = voucherAppliesToSelectedTier && appliedVoucher!.discountKind === 'percent'
            ? Math.round(selectedTierPrice * (appliedVoucher!.percentOff || 0) / 100)
            : (voucherAppliesToSelectedTier && appliedVoucher!.discountKind === 'free_wash' ? selectedTierPrice : 0);
          const finalCents = Math.max(0, selectedTierPrice - discountCents);
          // Stacked math: promo discounts first, credit covers the remainder,
          // the card pays whatever is left (mirrors the backend's draw order).
          const baseAfterPromo = voucherAppliesToSelectedTier ? finalCents : selectedTierPrice;
          const { draw: creditDraw, card: tierCardCents } = creditSplit(baseAfterPromo);
          const rewardsUsed = (voucherAppliesToSelectedTier ? discountCents : 0) + creditDraw;
          // 1.4 P20: add-ons ride the same charge (plan-included ones are free).
          // 1.4 R05: plan policy hides 'none' add-ons for members; voucher free add-ons and plan-included ones are free;
          // a percent promo scoped to wash + add-ons discounts the chargeable add-ons too.
          const memberPath = !!subCheck?.canWash && !selectedFleetId;
          const upsellMenu = (bay.upsells ?? []).filter(u => !(memberPath && u.upsellId && bay.planUpsellPolicy?.[u.upsellId] === 'none'));
          const voucherFree = new Set(voucherAppliesToSelectedTier ? (appliedVoucher?.freeUpsellIds ?? []) : []);
          const includedIds = new Set([...(bay.includedUpsellIds ?? []), ...(memberPath ? Object.entries(bay.planUpsellPolicy ?? {}).filter(([, v]) => v === 'included').map(([k]) => k) : []), ...voucherFree]);
          const upsellPct = voucherAppliesToSelectedTier && appliedVoucher?.discountKind === 'percent' && appliedVoucher.discountScope === 'wash_and_upsells' ? Math.min(100, appliedVoucher.percentOff ?? 0) : 0;
          const upsellChargeCents = upsellMenu.filter(u => selectedUpsells.includes(u.actionId) && !(u.upsellId && includedIds.has(u.upsellId))).reduce((a, u) => a + (u.priceCents - Math.round(u.priceCents * upsellPct / 100)), 0);
          const cardCents = tierCardCents + upsellChargeCents;
          const baseButtonLabel = selectedFleetId
            ? `Charge to Fleet — $${((selectedTierPrice + upsellChargeCents) / 100).toFixed(2)}`
            : isFullGift && upsellChargeCents === 0
              ? 'Redeem Free Wash'
              : cardCents === 0 && rewardsUsed > 0
                ? 'Start Wash — Free (rewards applied)'
                : rewardsUsed > 0
                  ? `Pay $${(cardCents / 100).toFixed(2)} (rewards −$${(rewardsUsed / 100).toFixed(2)})`
                  : `Pay $${((selectedTierPrice + upsellChargeCents) / 100).toFixed(2)}`;
          const blocked = !!availability && (availability.outOfService || (availability.inUse && !availability.queued) || availability.reason === 'controller_offline' || availability.reason === 'not_configured');
          const queueing = !!availability && availability.inUse && availability.queued && !availability.outOfService;
          const buttonLabel = queueing ? `Queue my wash · ${baseButtonLabel}` : baseButtonLabel;
          return (
          <Card style={styles.selectorCard}>
            <Text style={styles.selectorTitle}>Select Your Wash</Text>
            {/* One-line upsell when any purchasable bundle pairs a wash with
                vacuum minutes. The Bundles section renders ABOVE this card. */}
            {availableBundles.some(b => b.items?.some(i => i.kind === 'vac_minutes')) && (
              <Text style={{ fontSize: fontSize.xs, color: colors.primary, fontWeight: fontWeight.medium, marginBottom: spacing.sm }}>
                💡 Bundle your wash with vacuum minutes and save — see the Bundles section above.
              </Text>
            )}
            {visibleTiers.map((tier, i) => {
              // Reflect an applied gift/promo on this tier's price. Applies to all
              // tiers when the voucher isn't tier-scoped, else only the matching tier.
              const vApplies = !!appliedVoucher
                && (appliedVoucher.tierName == null || appliedVoucher.tierName === tier.name);
              const tierDiscount = vApplies
                ? (appliedVoucher!.discountKind === 'percent'
                    ? Math.round(tier.price * (appliedVoucher!.percentOff || 0) / 100)
                    : appliedVoucher!.discountKind === 'free_wash' ? tier.price : 0)
                : 0;
              const tierFinal = Math.max(0, tier.price - tierDiscount);
              const selected = selectedTier === tier.name;
              const freeVac = tier.freeVacMinutes ?? 0;
              return (
              <View key={tier.name}>
              <TouchableOpacity
                style={[styles.tierOption, selected && styles.tierSelected, selected && freeVac > 0 && { marginBottom: 2 }]}
                onPress={() => setSelectedTier(tier.name)}
                accessibilityRole="radio"
                accessibilityState={{ selected, checked: selected }}
                accessibilityLabel={`${tier.name}, $${(tierFinal / 100).toFixed(2)}`}
              >
                <View style={styles.tierLeft}>
                  <View style={[styles.tierRadio, selected && styles.tierRadioSelected]}>
                    {selected && <View style={styles.tierRadioDot} />}
                  </View>
                  <View>
                    <Text style={[styles.tierName, selected && styles.tierNameSelected]}>{tier.name}</Text>
                    {i === popularIdx && <Text style={styles.tierBadge}>Most Popular</Text>}
                  </View>
                </View>
                {tierDiscount > 0 ? (
                  <View style={styles.tierPriceRow}>
                    <Text style={styles.tierPriceStrike}>${(tier.price / 100).toFixed(2)}</Text>
                    <Text style={[styles.tierPrice, selected && styles.tierPriceSelected, { color: colors.success }]}>
                      {tierFinal === 0 ? 'FREE' : `$${(tierFinal / 100).toFixed(2)}`}
                    </Text>
                  </View>
                ) : (
                  <Text style={[styles.tierPrice, selected && styles.tierPriceSelected]}>
                    ${(tier.price / 100).toFixed(2)}
                  </Text>
                )}
              </TouchableOpacity>
              {/* Free-vac perk note (wave 2) — real tier + window values.
                  Wave 3: only under the SELECTED tier; rendered inside this
                  tier's own row group so it can't read as global. */}
              {selected && freeVac > 0 && (
                <View style={styles.tierFreeVacRow}>
                  <Ionicons name="sparkles" size={12} color={colors.primary} />
                  <Text style={styles.tierFreeVacText}>
                    This wash includes {freeVac} free vacuum minute{freeVac === 1 ? '' : 's'} — available
                    for {formatGrantWindow(bay.vacGrantWindowMinutes ?? 120)} after purchase
                  </Text>
                </View>
              )}
              </View>
              );
            })}
            {/* 1.4 P20: add-on picker */}
            {upsellMenu.length > 0 && (
              <View style={styles.upsellBox}>
                <Text style={styles.upsellTitle}>Add-ons</Text>
                {upsellMenu.map(u => {
                  const on = selectedUpsells.includes(u.actionId);
                  const included = !!u.upsellId && includedIds.has(u.upsellId);
                  return (
                    <TouchableOpacity key={u.actionId} style={styles.upsellRow} onPress={() => setSelectedUpsells(on ? selectedUpsells.filter(x => x !== u.actionId) : [...selectedUpsells, u.actionId])}
                      accessibilityRole="checkbox" accessibilityState={{ checked: on }} accessibilityLabel={`${u.name}, ${included ? 'included in your plan' : `$${(u.priceCents / 100).toFixed(2)}`}`}>
                      <Ionicons name={on ? 'checkbox' : 'square-outline'} size={22} color={on ? colors.primary : colors.textSecondary} />
                      <Text style={styles.upsellName}>{u.name}</Text>
                      {included ? <View style={styles.includedBadge}><Text style={styles.includedText}>{u.upsellId && voucherFree.has(u.upsellId) ? 'Free with your promo' : 'Included in your plan'}</Text></View> : <Text style={styles.upsellPrice}>${((u.priceCents - Math.round(u.priceCents * upsellPct / 100)) / 100).toFixed(2)}</Text>}
                    </TouchableOpacity>
                  );
                })}
                {selectedUpsells.length > 0 && (
                  <Text style={styles.upsellTotal}>Add-ons: {upsellChargeCents > 0 ? `$${(upsellChargeCents / 100).toFixed(2)}` : 'included'}{subCheck?.canWash && upsellChargeCents > 0 ? ' (charged to your saved card with a membership wash)' : ''}</Text>
                )}
              </View>
            )}
            {/* 1.4 P20: equipment state banner */}
            {availability && availability.reason !== 'ok' && availability.reason !== 'no_controller' && (
              <View style={[styles.availBanner, availability.outOfService || blocked ? styles.availBannerBlock : styles.availBannerInfo]}>
                <Ionicons name={availability.outOfService ? 'close-circle' : availability.reason === 'controller_offline' ? 'wifi-outline' : availability.reason === 'not_configured' ? 'construct-outline' : 'time-outline'} size={18} color={availability.outOfService || blocked ? colors.danger : colors.primary} />
                <Text style={styles.availText}>
                  {availability.outOfService ? (availability.message || 'This bay is out of service.')
                    : availability.reason === 'controller_offline' ? 'Wash equipment is offline. Please try again shortly.'
                    : availability.reason === 'not_configured' ? 'This bay is not set up yet. Please choose another bay.'
                    : availability.updateRequiredControllerIds?.length ? 'This bay is being updated. Please try again in a few minutes.'
                    : queueing ? 'This bay is in use. Your wash will queue and start as soon as it is free.'
                    : 'Bay is in use, please wait.'}
                </Text>
              </View>
            )}
            <View style={styles.purchaseSection}>
              <Button
                title={blocked ? (availability?.outOfService ? 'Out of service' : availability?.reason === 'controller_offline' ? 'Equipment offline' : availability?.reason === 'not_configured' ? 'Not set up yet' : 'Bay in use') : buttonLabel}
                onPress={() => {
                  if (blocked) return;
                  if (selectedFleetId && selectedTier) {
                    const price = visibleTiers.find(t => t.name === selectedTier)?.price || 0;
                    handleFleetPurchase(selectedTier, price);
                  } else {
                    handlePurchaseAutomatic();
                  }
                }}
                loading={purchasing}
                disabled={blocked}
                size="lg"
                style={styles.purchaseBtn}
              />
            </View>
          </Card>
          );
        })()}

        <View style={styles.secureRow}>
          <Ionicons name="lock-closed" size={14} color={colors.textLight} />
          <Text style={styles.secureText}>{selectedFleetId ? 'Charged to fleet account' : 'Payments secured by Stripe'}</Text>
        </View>
        <PageFooter />
      </ScrollView>

      {/* 1.4 P20: queued wash — waiting for the bay to free up */}
      <Modal visible={queuedWait !== null} transparent animationType="fade" onRequestClose={() => {}}>
        <View style={styles.successBackdrop}>
          <View style={styles.successCard}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={[styles.successHeadline, { marginTop: spacing.md }]}>Waiting for the bay to free up</Text>
            <Text style={styles.successDetailText}>
              {queuedWait?.message || 'Your wash is paid and queued.'} It will start automatically when the bay is free (up to {Math.round((bay?.inUseMaxWaitMs ?? 900000) / 60000)} min). If it does not start in time we refund you automatically.
            </Text>
          </View>
        </View>
      </Modal>

      {/* Prominent purchase-success confirmation — custom transparent Modal
          (multi-button Alert.alert collapses on web). Makes a completed bundle
          or membership purchase unmissable so nobody buys twice by accident. */}
      <Modal
        visible={purchaseSuccess !== null}
        transparent
        animationType="fade"
        onRequestClose={() => {
          if (purchaseSuccess?.kind === 'bundle') setPendingBundleFocus(true);
          // New membership → collect the registered vehicle right away (keyed
          // off the purchase itself, not this bay's coverage).
          if (purchaseSuccess?.kind === 'subscription' && purchasedSub && !purchasedSub.vehicle) setVehicleModalOpen(true);
          setPurchaseSuccess(null);
        }}
      >
        <View style={styles.successBackdrop}>
          <View style={styles.successCard}>
            <View style={styles.successIconRing}>
              <Ionicons name="checkmark-circle" size={72} color={colors.success} />
            </View>
            <Text style={styles.successHeadline}>{purchaseSuccess?.headline}</Text>
            <View style={styles.successDetailBadge}>
              <Ionicons name={purchaseSuccess?.kind === 'bundle' ? 'cube' : 'ribbon'} size={16} color={colors.primary} />
              <Text style={styles.successDetailText}>{purchaseSuccess?.detail}</Text>
            </View>
            <Text style={styles.successReadyLine}>{purchaseSuccess?.readyLine}</Text>
            <Button
              title="Done"
              size="lg"
              style={{ width: '100%', marginTop: spacing.lg }}
              onPress={() => {
                // Re-arm the scroll-to-pack so the fresh bundle is front and
                // center the moment the modal drops away.
                if (purchaseSuccess?.kind === 'bundle') setPendingBundleFocus(true);
                // New membership → collect the registered vehicle right away
                // (keyed off the purchase itself, not this bay's coverage).
                if (purchaseSuccess?.kind === 'subscription' && purchasedSub && !purchasedSub.vehicle) setVehicleModalOpen(true);
                setPurchaseSuccess(null);
              }}
            />
          </View>
        </View>
      </Modal>

      {/* Pre-redemption vehicle confirmation — the membership is valid for one
          registered vehicle, so every redemption confirms it's actually here.
          Custom transparent Modal (multi-button Alert.alert collapses on web). */}
      <Modal
        visible={redeemConfirm !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setRedeemConfirm(null)}
      >
        <View style={styles.successBackdrop}>
          <View style={styles.successCard}>
            <Ionicons name="car-sport" size={48} color={colors.primary} />
            <Text style={styles.successHeadline}>Confirm Your Vehicle</Text>
            {subCheck?.vehicle && (
              <Text style={styles.vehicleConfirmLine}>
                You are redeeming for:{' '}
                <Text style={{ fontWeight: fontWeight.bold, color: colors.text }}>
                  {formatVehicleLine(subCheck.vehicle)}
                </Text>
              </Text>
            )}
            <Button
              title="Yes, this vehicle is here"
              size="lg"
              style={{ width: '100%', marginTop: spacing.lg }}
              icon={<Ionicons name="checkmark-circle" size={20} color={colors.white} />}
              onPress={() => {
                const kind = redeemConfirm;
                setRedeemConfirm(null);
                if (kind === 'automatic') handleSubscriptionWash();
                else if (kind === 'selfserve') handleSubscriptionSelfServeStart();
              }}
            />
            <Button
              title="Cancel"
              variant="outline"
              size="sm"
              style={{ width: '100%', marginTop: spacing.sm }}
              onPress={() => setRedeemConfirm(null)}
            />
            {subCheck?.vehicle?.canEdit ? (
              <TouchableOpacity
                onPress={() => { setRedeemConfirm(null); setVehicleModalOpen(true); }}
                hitSlop={8}
                style={{ marginTop: spacing.md }}
              >
                <Text style={styles.vehicleChangeLink}>Different vehicle? Update your profile</Text>
              </TouchableOpacity>
            ) : subCheck?.vehicle?.nextEditAt ? (
              <Text style={styles.vehicleLockNote}>
                Vehicle changes unlock {new Date(subCheck.vehicle.nextEditAt).toLocaleDateString()}. Contact the wash if this is wrong.
              </Text>
            ) : null}
          </View>
        </View>
      </Modal>

      {/* Vehicle profile create/edit (membership signup + first redemption) */}
      <VehicleProfileModal
        visible={vehicleModalOpen}
        mode="customer"
        initial={subCheck?.vehicle ?? purchasedSub?.vehicle ?? null}
        contextLine={subCheck?.planName ? `${subCheck.planName} membership` : undefined}
        onSubmit={handleVehicleSave}
        onClose={() => { setVehicleModalOpen(false); setPendingRedeemKind(null); }}
      />

      {/* Shared wash complete overlay with rating */}
      {washComplete && (
        <WashCompleteOverlay
          data={washComplete}
          onDone={() => { setWashComplete(null); router.replace('/'); }}
          onReportProblem={() => { setReportTransactionId(washComplete.transactionId || washComplete.washId); setShowReport(true); }}
        />
      )}

      <ReportProblemModal
        visible={showReport}
        onClose={() => setShowReport(false)}
        transactionId={reportTransactionId}
        locationName={location?.name}
        bayName={bay?.name}
        amount={washComplete?.amount}
        type={bay?.type}
        tierName={washComplete?.tierName}
      />
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  upsellBox: { marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: c.borderLight },
  upsellTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  upsellRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xs },
  upsellName: { flex: 1, fontSize: fontSize.sm, color: c.text },
  upsellPrice: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  includedBadge: { paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: 10, backgroundColor: c.success + '18', borderWidth: 1, borderColor: c.success },
  includedText: { fontSize: 10, fontWeight: fontWeight.semibold, color: c.success },
  upsellTotal: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: spacing.xs },
  availBanner: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.sm, borderRadius: borderRadius.md, marginTop: spacing.sm, borderWidth: 1 },
  availBannerBlock: { backgroundColor: c.danger + '12', borderColor: c.danger },
  availBannerInfo: { backgroundColor: c.primary + '12', borderColor: c.primary },
  availText: { flex: 1, fontSize: fontSize.sm, color: c.text },
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md, paddingBottom: spacing.xxl },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },
  loadingText: { fontSize: fontSize.md, color: c.textSecondary, marginTop: spacing.md },

  downloadBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    padding: spacing.md, borderRadius: borderRadius.md, marginBottom: spacing.md,
    backgroundColor: c.primaryBg, borderWidth: 1, borderColor: c.primary + '30',
  },
  downloadBannerTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.text },
  downloadBannerText: { fontSize: fontSize.xs, color: c.textSecondary },
  downloadBannerBtn: {
    backgroundColor: c.primary, paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    borderRadius: borderRadius.sm,
  },
  downloadBannerBtnText: { color: c.white, fontSize: fontSize.sm, fontWeight: fontWeight.semibold },

  statusBanner: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md, borderRadius: borderRadius.md, marginBottom: spacing.md },
  statusSuccess: { backgroundColor: c.success + '10', borderWidth: 1, borderColor: c.success + '30' },
  statusError: { backgroundColor: c.danger + '10', borderWidth: 1, borderColor: c.danger + '30' },
  statusInfo: { backgroundColor: c.primary + '10', borderWidth: 1, borderColor: c.primary + '30' },
  statusText: { flex: 1, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },

  locationBox: { backgroundColor: '#733e24', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.md },
  locationBoxHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm },
  locationBoxName: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.white, flex: 1 },
  locationBoxAddress: { fontSize: fontSize.sm, color: 'rgba(255,255,255,0.85)', marginBottom: spacing.xs },
  locationBoxDesc: { fontSize: fontSize.sm, color: 'rgba(255,255,255,0.75)', marginBottom: spacing.sm, lineHeight: 20 },
  locationBoxRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  locationBoxDetail: { fontSize: fontSize.sm, color: 'rgba(255,255,255,0.85)' },
  locationBoxHours: { marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.2)' },
  locationBoxHoursRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 },
  locationBoxHoursDay: { fontSize: fontSize.sm, color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' },

  bayCard: { marginBottom: spacing.md },
  bayHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  bayName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, flex: 1 },
  bayTypeBadge: { backgroundColor: c.primaryBg, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.full },
  bayTypeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.primaryDark },

  selectorCard: { marginBottom: spacing.md },
  selectorTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  selfServeInfo: { alignItems: 'center', paddingVertical: spacing.md },
  selfServeTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.md },
  selfServeDesc: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs, maxWidth: rs(280), lineHeight: 20 },
  rateBox: { alignItems: 'center', marginTop: spacing.lg, backgroundColor: c.primaryBg, paddingHorizontal: spacing.xl, paddingVertical: spacing.md, borderRadius: borderRadius.lg },
  rateAmount: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.primary },
  rateUnit: { fontSize: fontSize.sm, color: c.textSecondary },
  minChargeText: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.sm, textAlign: 'center' },
  startBtn: { marginTop: spacing.lg, backgroundColor: c.success, shadowColor: c.success, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 4 },

  preAuthText: { fontSize: fontSize.xs, color: c.textLight, textAlign: 'center', marginTop: spacing.md, maxWidth: rs(280) },
  progressBarContainer: { width: '80%', maxWidth: rs(300), height: 6, backgroundColor: c.borderLight, borderRadius: 3, marginTop: spacing.md, overflow: 'hidden' as const },
  progressBarFill: { height: '100%', backgroundColor: c.primary, borderRadius: 4 },

  timerContainer: { flex: 1, backgroundColor: c.background, alignItems: 'center', justifyContent: 'center', padding: spacing.lg },
  timerPulse: { width: rs(60), height: rs(60), borderRadius: rs(30), backgroundColor: c.primaryBg, justifyContent: 'center', alignItems: 'center', marginBottom: spacing.md },
  timerLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.bold, color: c.primaryDark, letterSpacing: 2 },
  timerDisplay: { fontSize: fs(44), fontWeight: fontWeight.bold, color: c.text, fontVariant: ['tabular-nums'] },
  timerSubtext: { fontSize: fontSize.sm, color: c.textLight, marginTop: -spacing.xs },
  costDisplay: { alignItems: 'center', marginTop: spacing.md, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, borderRadius: borderRadius.xl, borderWidth: 2, minWidth: rs(200), gap: 2 },
  costDisplayMet: { backgroundColor: c.surface, borderColor: c.success },
  costDisplayNotMet: { backgroundColor: c.surface, borderColor: c.danger },
  costLabel: { fontSize: fontSize.md, color: c.primaryDark, fontWeight: fontWeight.semibold },
  costLabelWarning: { fontSize: fontSize.md, color: c.secondary, fontWeight: fontWeight.semibold, textAlign: 'center' },
  costAmount: { fontSize: fontSize.hero, fontWeight: fontWeight.bold, color: c.text },
  costRate: { fontSize: fontSize.sm, color: c.textLight, marginTop: spacing.xs },
  washInfo: { marginTop: spacing.md, gap: 2 },
  washInfoText: { fontSize: fontSize.sm, color: c.textSecondary },
  stopButton: { alignItems: 'center', marginTop: spacing.md },
  stopButtonInner: { width: rs(56), height: rs(56), borderRadius: rs(28), backgroundColor: c.danger, justifyContent: 'center', alignItems: 'center', shadowColor: c.danger, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.4, shadowRadius: 8, elevation: 6 },
  stopButtonLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.xs },
  timerAlert: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.md, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, maxWidth: rs(300) },
  timerAlertError: { backgroundColor: c.secondaryBg, borderWidth: 1, borderColor: c.warning },
  timerAlertSuccess: { backgroundColor: c.primaryBg, borderWidth: 1, borderColor: c.primary },
  timerAlertText: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium, flex: 1 },

  tierOption: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderRadius: borderRadius.md, borderWidth: 2, borderColor: c.border, marginBottom: spacing.sm },
  tierSelected: { borderColor: c.primary, backgroundColor: c.primaryBg },
  tierLeft: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  tierRadio: { width: rs(22), height: rs(22), borderRadius: rs(11), borderWidth: 2, borderColor: c.border, justifyContent: 'center', alignItems: 'center' },
  tierRadioSelected: { borderColor: c.primary },
  tierRadioDot: { width: rs(12), height: rs(12), borderRadius: rs(6), backgroundColor: c.primary },
  tierName: { fontSize: fontSize.md, fontWeight: fontWeight.medium, color: c.text },
  tierNameSelected: { color: c.primary, fontWeight: fontWeight.semibold },
  tierBadge: { fontSize: fontSize.xs, color: c.success, fontWeight: fontWeight.medium, marginTop: 2 },
  tierPrice: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.textSecondary },
  tierPriceSelected: { color: c.primary },
  tierPriceRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  tierPriceStrike: { fontSize: fontSize.sm, color: c.textLight, textDecorationLine: 'line-through' },
  // Free-vac perk note under a tier row (wave 2)
  tierFreeVacRow: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 4,
    paddingHorizontal: spacing.md, marginBottom: spacing.sm,
  },
  tierFreeVacText: { flex: 1, fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.medium, lineHeight: 16 },
  purchaseSection: { marginTop: spacing.sm },
  purchaseBtn: { shadowColor: c.primary, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.2, shadowRadius: 8, elevation: 4 },
  secureRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, marginTop: spacing.md },
  secureText: { fontSize: fontSize.xs, color: c.textLight },

  inactiveContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, backgroundColor: c.background },
  inactiveIcon: { width: rs(120), height: rs(120), borderRadius: rs(60), backgroundColor: c.borderLight, justifyContent: 'center', alignItems: 'center', marginBottom: spacing.lg },
  inactiveTitle: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.sm },
  inactiveSubtext: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center', maxWidth: rs(300), lineHeight: 22 },
  inactiveHint: { fontSize: fontSize.sm, color: c.textLight, marginTop: spacing.sm, textAlign: 'center' },
  inactiveBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xl, paddingVertical: spacing.md + 4, paddingHorizontal: spacing.xl, borderRadius: borderRadius.md, borderWidth: 1.5, borderColor: c.primary, minHeight: rs(48) },
  inactiveBtnText: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.primaryDark },

  // Preset styles
  presetGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.md, marginBottom: spacing.md, justifyContent: 'center' },
  presetBtn: { minWidth: rs(100), paddingVertical: spacing.md, paddingHorizontal: spacing.lg, borderRadius: borderRadius.lg, borderWidth: 2, borderColor: c.border, alignItems: 'center', backgroundColor: c.surface },
  presetBtnSelected: { borderColor: c.primary, backgroundColor: c.primaryBg },
  presetPrice: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  presetPriceSelected: { color: c.primary },
  presetTime: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  presetTimeSelected: { color: c.primaryDark },
  customAmountRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md, justifyContent: 'center' },
  customAmountLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  customAmountInput: { flexDirection: 'row', alignItems: 'center', borderWidth: 1.5, borderColor: c.border, borderRadius: borderRadius.md, paddingHorizontal: spacing.sm, backgroundColor: c.surface },
  customAmountPrefix: { fontSize: fontSize.md, color: c.textSecondary, fontWeight: fontWeight.semibold },
  customAmountField: { fontSize: fontSize.md, color: c.text, paddingVertical: spacing.sm, paddingHorizontal: spacing.xs, minWidth: rs(80) },
  customAmountError: { fontSize: fontSize.xs, color: c.danger, marginBottom: spacing.xs, textAlign: 'center' },
  customAmountBounds: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.md, textAlign: 'center' },

  // Subscription wash success overlay
  subSuccessOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: c.surface,
    justifyContent: 'center', alignItems: 'center',
    zIndex: 100,
    padding: spacing.xl,
  },
  subSuccessContent: {
    alignItems: 'center', width: '100%', maxWidth: rs(360),
  },
  subSuccessIconRing: {
    width: rs(120), height: rs(120), borderRadius: rs(60),
    backgroundColor: c.success + '15',
    justifyContent: 'center', alignItems: 'center',
    marginBottom: spacing.lg,
  },
  subSuccessTitle: {
    fontSize: fs(28), fontWeight: fontWeight.bold, color: c.success,
    marginBottom: spacing.xs,
  },
  subSuccessSubtitle: {
    fontSize: fontSize.md, color: c.textSecondary,
    marginBottom: spacing.md, textAlign: 'center',
  },
  subSuccessPlanBadge: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary + '10',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.full, marginBottom: spacing.xl,
  },
  subSuccessPlanText: {
    fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primary,
  },
  subSuccessStats: {
    flexDirection: 'row', width: '100%',
    backgroundColor: c.background, borderRadius: borderRadius.lg,
    padding: spacing.lg,
  },
  subSuccessStatBox: {
    flex: 1, alignItems: 'center',
  },
  subSuccessStatNumber: {
    fontSize: fs(32), fontWeight: fontWeight.bold, color: c.primary,
  },
  subSuccessStatLabel: {
    fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2,
  },
  subSuccessStatDetail: {
    fontSize: fontSize.xs, color: c.textLight, marginTop: 2,
  },
  subSuccessStatDivider: {
    width: 1, backgroundColor: c.borderLight, marginHorizontal: spacing.sm,
  },

  // Automatic wash success overlay
  autoSuccessAmountBadge: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary + '10',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.full, marginBottom: spacing.xl,
  },
  autoSuccessLocationBox: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    backgroundColor: c.background, borderRadius: borderRadius.lg,
    padding: spacing.lg, width: '100%',
  },
  autoSuccessLocationName: {
    fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text,
  },
  autoSuccessLocationDetail: {
    fontSize: fontSize.sm, color: c.textSecondary, marginTop: 2,
  },

  // Controller offline warning banner
  offlineBanner: {
    backgroundColor: '#fffbeb',
    borderWidth: 1, borderColor: '#fbbf24',
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  offlineBannerContent: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
  },
  offlineBannerTitle: {
    fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: '#92400e',
  },
  offlineBannerText: {
    fontSize: fontSize.xs, color: '#a16207', marginTop: 2,
  },
  offlineBannerBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    alignSelf: 'flex-end',
    marginTop: spacing.sm,
    paddingVertical: 4, paddingHorizontal: spacing.sm,
    borderRadius: borderRadius.sm,
    backgroundColor: '#fef3c7',
  },
  offlineBannerBtnText: {
    fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: '#92400e',
  },
  midWashWarning: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    backgroundColor: '#fef3c7', padding: spacing.sm, borderRadius: borderRadius.sm,
    marginVertical: spacing.sm, maxWidth: rs(300),
  },
  midWashWarningText: {
    fontSize: fontSize.xs, color: '#d97706', flex: 1,
  },

  // Owned-pack marker on purchasable bundle cards
  ownedBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    alignSelf: 'flex-start',
    backgroundColor: c.success + '15',
    borderWidth: 1, borderColor: c.success + '40',
    paddingHorizontal: spacing.sm, paddingVertical: 3,
    borderRadius: borderRadius.full, marginBottom: spacing.xs,
  },
  ownedBadgeText: {
    fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.success,
  },

  // Purchase success confirmation modal
  successBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center', padding: spacing.lg,
  },
  successCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.lg,
    padding: spacing.xl, width: '100%', maxWidth: rs(360),
    alignItems: 'center',
  },
  successIconRing: {
    width: rs(104), height: rs(104), borderRadius: rs(52),
    backgroundColor: c.success + '15',
    justifyContent: 'center', alignItems: 'center',
    marginBottom: spacing.md,
  },
  successHeadline: {
    fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text,
    textAlign: 'center',
  },
  successDetailBadge: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary + '10',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.full, marginTop: spacing.md,
  },
  successDetailText: {
    fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primary,
    flexShrink: 1, textAlign: 'center',
  },
  successReadyLine: {
    fontSize: fontSize.sm, color: c.textSecondary,
    textAlign: 'center', marginTop: spacing.md, lineHeight: 20,
  },

  vehicleConfirmLine: {
    fontSize: fontSize.md, color: c.textSecondary,
    textAlign: 'center', marginTop: spacing.md, lineHeight: 22,
  },
  // Metered-vacuum bundle minutes stepper
  minuteStepperRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: spacing.lg, marginBottom: spacing.md,
  },
  minuteStepBtn: {
    width: rs(44), height: rs(44), borderRadius: rs(22),
    borderWidth: 2, borderColor: c.primary,
    justifyContent: 'center', alignItems: 'center',
  },
  minuteStepperValue: { fontSize: fs(28), fontWeight: fontWeight.bold, color: c.text, fontVariant: ['tabular-nums'] },

  vehicleChangeLink: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.primary },
  vehicleLockNote: {
    fontSize: fontSize.xs, color: c.textLight,
    textAlign: 'center', marginTop: spacing.md,
  },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
